/**
 * Bosnian strings for the owner screens.
 *
 * The base every other language is measured against. Enum labels are NOT
 * here and must never be — statuses, types, priorities, roles and months
 * come from /api/meta, and a second copy is the drift that put IN_PROGRESS
 * on a manager's badge in the legacy app.
 *
 * This slice is unusual in one respect: almost nobody reads it. The audience
 * is the two or three people who run the platform, not the floor — so the
 * copy is allowed to be blunt in a way a worker-facing screen is not.
 * "Tišina je naša odluka, ne njihova" on a suspended customer, or "Moguće
 * pogođena lozinka" over a run of failed sign-ins, has to survive translation
 * as a sentence with an edge, not as a neutral caption.
 */

export const owner = {
  // ---- Shared vocabulary --------------------------------------------
  // Words that appear on more than one owner screen. One key each rather
  // than a copy per screen: "Preuzmi" is the same act in three places, and
  // three keys is three chances for two of them to drift apart.
  'owner.panelName': 'Vlasnički panel',
  'owner.value.never': 'nikad',
  'owner.action.cancel': 'Odustani',
  'owner.action.save': 'Sačuvaj',
  'owner.form.fullName': 'Ime i prezime',
  'owner.form.employeeCode': 'Šifra zaposlenog',
  'owner.form.email': 'E-mail',
  'owner.action.back': 'Nazad',

  /*
   * Counted nouns, in the three forms the language has: 1 / 2–4 / 5+.
   *
   * This console is mostly counts — users, work centres, accounts — and
   * every one of them was a template literal with the noun typed after the
   * number. That reads correctly for exactly the counts the author
   * happened to have on screen and is wrong for the rest: "2 korisnika" is
   * right, "2 korisnik" is not, and English needs two forms where this needs
   * three. Each phrase is one template per form per language, reached
   * through `tp()`, and the sentences they sit in interpolate the finished
   * phrase rather than adding to it.
   */
  'owner.count.users.one': '{count} korisnik',
  'owner.count.users.few': '{count} korisnika',
  'owner.count.users.many': '{count} korisnika',
  'owner.count.centres.one': '{count} centar',
  'owner.count.centres.few': '{count} centra',
  'owner.count.centres.many': '{count} centara',
  'owner.count.companies.one': '{count} firma',
  'owner.count.companies.few': '{count} firme',
  'owner.count.companies.many': '{count} firmi',
  'owner.count.suspended.one': '{count} obustavljena',
  'owner.count.suspended.few': '{count} obustavljene',
  'owner.count.suspended.many': '{count} obustavljenih',
  'owner.count.members.one': '{count} član tima',
  'owner.count.members.few': '{count} člana tima',
  'owner.count.members.many': '{count} članova tima',

  // ---- The console shell --------------------------------------------
  // Route names this client owns, not domain labels. The hints say what a
  // screen is *for*, which is the difference between "Analitika" meaning the
  // customer's factory and meaning whether the customer still exists.
  'owner.nav.overview': 'Pregled',
  'owner.nav.overview.hint': 'Šta se dešava na platformi',
  'owner.nav.companies': 'Firme',
  'owner.nav.companies.hint': 'Klijenti i njihova podešavanja',
  'owner.nav.analytics': 'Analitika',
  'owner.nav.analytics.hint': 'Prihvaćenost i odliv klijenata',
  'owner.nav.team': 'Tim',
  'owner.nav.team.hint': 'Ko vodi platformu i s kojim ovlaštenjima',
  'owner.nav.system': 'Sistem',
  'owner.nav.system.hint': 'Red čekanja, zadaci, disk, prijave',
  'owner.nav.aria': 'Navigacija panela',

  'owner.account.aria': 'Nalog — {name}',
  'owner.account.myAccount': 'Moj nalog',
  'owner.account.signOut': 'Odjava',

  'owner.footer.copyright': 'KaProLean · vlasnički panel · © {year}',
  'owner.footer.software': 'Softver:',

  // ---- Dashboard ----------------------------------------------------
  'owner.dashboard.loading': 'Učitavanje pregleda…',
  'owner.dashboard.title': 'Pregled platforme',
  'owner.dashboard.subtitle': 'Sve firme zajedno. Osvježava se svakih minut.',

  // ---- Companies ----------------------------------------------------
  'owner.companies.title': 'Firme',
  'owner.companies.subtitle': 'Klijenti na ovoj instalaciji.',
  'owner.companies.new': 'Nova firma',
  'owner.companies.search.placeholder': 'Ime ili šifra firme',
  'owner.companies.search.aria': 'Pretraga firmi',
  'owner.companies.loading': 'Učitavanje firmi…',
  'owner.companies.empty.title': 'Nema firmi',
  'owner.companies.empty.description': 'Nijedna firma ne odgovara pretrazi.',
  // Quotes the button by name, so the two have to move together — if the
  // button is renamed in a language, this sentence points at nothing.
  'owner.companies.empty.hint': 'Kreirajte prvu firmu dugmetom „Nova firma“.',
  'owner.companies.impersonate.description': 'Odaberite korisnika čiji ekran želite vidjeti.',

  'owner.companies.saved.created': 'Firma je kreirana.',
  'owner.companies.saved.updated': 'Firma je izmijenjena.',

  'owner.companies.form.description': 'Podaci klijenta i njihova podešavanja.',
  'owner.companies.form.code': 'Šifra',
  'owner.companies.form.code.hint':
    'Velika slova i brojevi. Pojavljuje se u izvještajima i nazivima datoteka.',
  'owner.companies.form.name': 'Naziv',
  'owner.companies.form.contactEmail': 'Kontakt e-mail',
  'owner.companies.form.contactPhone': 'Kontakt telefon',
  'owner.companies.form.logo': 'Logo (URL)',
  'owner.companies.form.logo.hint':
    'Adresa slike koju klijent sam hostuje. Pojavljuje se u bočnoj traci njihove aplikacije.',
  // An example, not a value — so the domain is a made-up one in whatever
  // language is reading, rather than a real customer's address.
  'owner.companies.form.logo.placeholder': 'https://firma.ba/logo.svg',
  'owner.companies.form.logoDark': 'Logo za tamnu temu (URL)',
  'owner.companies.form.logoDark.hint':
    'Nije obavezno. Logo sa tamnim slovima se na tamnoj temi ne vidi — bez ove varijante prikazujemo ga na bijeloj podlozi.',
  'owner.companies.form.logoDark.placeholder': 'https://firma.ba/logo-bijeli.svg',
  'owner.companies.form.currency': 'Valuta',
  'owner.companies.form.currency.hint':
    'U čemu ova firma računa uštede, troškove i nagrade. Postavlja se ovdje pri otvaranju firme, a njihov administrator je poslije može promijeniti sam.',
  'owner.companies.form.maxUpload': 'Maksimalna veličina datoteke (MB)',
  'owner.companies.form.maxUpload.hint': 'Odnosi se na jedan video ili sliku uz prijavu.',

  'owner.companies.form.admin.legend': 'Administrator firme',
  'owner.companies.form.admin.note':
    'Na ovu adresu šaljemo pozivnicu sa linkom na kojem sami postavljaju lozinku. Lozinku ne unosite vi i niko je ne vidi.',
  'owner.companies.form.admin.employeeCode.hint': 'Time se prijavljuju — ne e-mailom.',

  // ---- Impersonation ------------------------------------------------
  'owner.impersonate.action': 'Preuzmi',
  'owner.impersonate.started': 'Prijavljeni ste kao {name}.',
  'owner.impersonate.notice':
    'Sesija traje jedan sat i vidljiva je korisniku. Sve što uradite bilježi se u dnevniku firme pod vašim imenom.',
  'owner.impersonate.search.placeholder': 'Ime ili šifra radnika',
  'owner.impersonate.empty.title': 'Nema korisnika',
  'owner.impersonate.empty.description': 'Nijedan nalog ne odgovara pretrazi.',

  // ---- Analytics ----------------------------------------------------
  // The vendor's question, not the customer's. Every heading here is about
  // whether the thing is used at all — the customer's own analytics screen
  // is where the factory's numbers live.
  'owner.analytics.loading': 'Učitavanje analitike…',
  'owner.analytics.title': 'Analitika platforme',
  'owner.analytics.subtitle': 'Koliko se ono što smo prodali zaista koristi.',
  'owner.analytics.period.aria': 'Period',
  // 6 and 12 take the genitive plural, 3 and 24 do not — "posljednja 24
  // mjeseca". The option list is four fixed numbers falling on both sides of
  // the rule, so this could never have been one string.
  'owner.analytics.period.one': 'Posljednji {count} mjesec',
  'owner.analytics.period.few': 'Posljednja {count} mjeseca',
  'owner.analytics.period.many': 'Posljednjih {count} mjeseci',

  // What is kept of the platform-wide page under the portfolio — see
  // pages/owner/Analytics.tsx for what was folded into the table and why.
  'owner.analytics.monthly.title': 'Aktivnost po mjesecima',
  'owner.analytics.monthly.description':
    'Prijave, 5S evaluacije i novi nalozi, sve firme zajedno.',

  'owner.analytics.types.title': 'Vrste prijava',
  'owner.analytics.types.description': 'Šta ljudi zaista prijavljuju.',

  // Usually a very short list, and that is the finding: when one person
  // files most of a factory's reports, the rollout reached an enthusiast
  // rather than a floor.
  'owner.analytics.contributors.title': 'Ko nosi klijente',
  'owner.analytics.contributors.description': 'Najviše prijava po korisniku.',
  'owner.analytics.contributors.empty': 'Još nema nijedne prijave.',

  // The chart's own series names. Client-owned, unlike the month labels
  // under the bars and the type labels beside them, which arrive on the row.
  'owner.analytics.bars.submissions': 'Prijave',
  'owner.analytics.bars.evaluations': '5S',
  'owner.analytics.bars.newUsers': 'Novi nalozi',
  'owner.analytics.bars.tooltip': '{label}: {count}',

  // ---- System health ------------------------------------------------
  // The rule this screen is written to: never a green tick that means "not
  // checked". Anything the server could not observe says so in words, which
  // is why `notMeasurable` is a sentence fragment and not an em dash.
  'owner.health.loading': 'Provjera sistema…',
  'owner.health.title': 'Sistem',
  'owner.health.checkedAt': 'Provjereno {when}. Osvježava se svakih 30 sekundi.',
  'owner.health.notMeasurable': 'nije mjerljivo',
  'owner.health.minutes': '{count} min',

  'owner.health.queue.title': 'Red čekanja',
  'owner.health.queue.description':
    'Obrada e-maila i live obavještenja. Ako radnik stane, aplikacija i dalje radi — samo tiho prestane slati.',
  'owner.health.queue.pending': 'Na čekanju',
  'owner.health.queue.oldest': 'Najstariji posao čeka',
  'owner.health.queue.oldest.warning':
    'Posao čeka duže od deset minuta — provjerite da li kapro-queue radi.',
  'owner.health.queue.failed': 'Neuspjelih',
  'owner.health.queue.lastFailure': 'Posljednji neuspjeh {when}.',
  'owner.health.queue.depth': 'Dubina reda',

  'owner.health.scheduler.title': 'Raspored',
  'owner.health.scheduler.description': 'Dnevni podsjetnici i mjesečna 5S provjera.',
  'owner.health.scheduler.lastNotification': 'Posljednje obavještenje',

  'owner.health.storage.title': 'Prostor',
  'owner.health.storage.description': 'Disk „{disk}“ i veličina priloga.',
  'owner.health.storage.free': 'Slobodno',
  'owner.health.storage.total': 'od {total} ukupno',
  'owner.health.storage.notLocal': 'Putanja diska nije lokalna.',
  'owner.health.storage.attachments': 'Prilozi',

  'owner.health.database.title': 'Baza',
  'owner.health.database.description': 'Veza „{connection}“.',
  'owner.health.database.latency': 'Odziv',
  'owner.health.database.ms': '{count} ms',
  // The one that catches a half-finished deploy: files copied, migrate
  // skipped. The instruction is part of the string because the person
  // reading it is the person who has to run the command.
  'owner.health.database.pendingMigrations': 'Nepokrenutih migracija',
  'owner.health.database.pendingMigrations.warning':
    'Deploy je prekinut ili migracije nisu pokrenute. Pokrenite php artisan migrate.',

  // ---- Sparkline ----------------------------------------------------
  // The chart has no axes and no tooltip, so this label is the only thing a
  // screen reader gets — it has to carry the total, the window and the peak
  // in one sentence.
  'owner.sparkline.aria.one':
    '{count} prijava u posljednjih {days} dana, vrhunac {peak} u jednom danu.',
  'owner.sparkline.aria.few':
    '{count} prijave u posljednjih {days} dana, vrhunac {peak} u jednom danu.',
  'owner.sparkline.aria.many':
    '{count} prijava u posljednjih {days} dana, vrhunac {peak} u jednom danu.',
  'owner.sparkline.caption': '{total} ukupno · vrhunac {peak}',

  // The MES integration screen. See pages/owner/CompanyMes.tsx for why the
  // secret fields explain what blank means.
  'owner.mes.field.baseUrl': 'Adresa MES-a',
  'owner.mes.field.baseUrl.hint': 'Osnovna adresa, bez putanje. Npr. https://mes.firma.ba',
  'owner.mes.field.token': 'API token',
  'owner.mes.field.token.hint': 'Sanctum token koji vam je izdao MES.',
  'owner.mes.field.token.stored': 'Token je sačuvan ({hint}). Ostavite prazno da ga zadržite, upišite „null" da ga obrišete.',
  'owner.mes.secret.unchanged': 'nepromijenjeno',
  'owner.mes.field.plantIds': 'ID pogona',
  'owner.mes.field.plantIds.hint': 'Vrijednosti id_pogon iz MES-a, odvojene zarezom. Test ispod ih izlistava.',
  'owner.mes.webhooks.title': 'Događaji prema MES-u',
  'owner.mes.webhooks.note': 'Ovdje šaljemo prijave i evaluacije. MES mora imati endpoint koji ih prima i provjerava potpis.',
  'owner.mes.field.webhookUrl': 'Adresa za događaje',
  'owner.mes.field.webhookSecret': 'Zajednička tajna',
  'owner.mes.field.webhookSecret.hint': 'Njome potpisujemo svaki zahtjev, da MES može provjeriti da dolazi od nas.',
  'owner.mes.events.title': 'Koje događaje slati',
  'owner.mes.events.all': 'Trenutno se šalju svi događaji.',
  'owner.mes.events.chosen': 'Odabrano: {count}.',
  'owner.mes.action.test': 'Testiraj vezu',
  'owner.mes.action.sync': 'Sinhronizuj sada',
  'owner.mes.synced.counts': 'Preuzeto: {plants} pogona, {centres} radnih centara, {users} radnika.',
  'owner.mes.plants.title': 'Pogoni u MES-u',
  'owner.mes.deliveries.title': 'Posljednje isporuke',
  'owner.mes.deliveries.attempts': '{count} pokušaja',
  'owner.mes.failing.title': 'Isporuke ne prolaze',
  'owner.mes.failing.body': 'Neuspjelih isporuka: {count}. MES ih nije primio.',

  // ---- Roles on the vendor's own team ---------------------------------
  // Client-owned, unlike every customer role label: these three never leave
  // the console. Each description is one line and says what the role can
  // *not* do as plainly as what it can — that is the half people misjudge.
  'owner.role.full': 'Puni pristup',
  'owner.role.full.description': 'Sve: otvara i podešava firme, obustavlja ih i upravlja timom.',
  'owner.role.support': 'Podrška',
  'owner.role.support.description':
    'Vidi sve, preuzima naloge, prekida sesije i testira MES. Ne mijenja podešavanja firmi.',
  'owner.role.viewer': 'Samo pregled',
  'owner.role.viewer.description': 'Vidi sve, ništa ne mijenja i ne ulazi u tuđe naloge.',

  // A locked control names the reader's role and the roles that may — the
  // person reading wants to know whom to ask, not only that they cannot.
  'owner.locked': 'Vaša uloga ({role}) ovo ne dozvoljava. Mogu: {roles}.',
  'owner.readOnly': 'Samo za čitanje — vaša uloga ({role}) ovo ne mijenja. Mogu: {roles}.',

  // ---- Status and usage -------------------------------------------------
  'owner.status.active': 'Aktivna',
  'owner.status.suspended': 'Obustavljena',
  'owner.usage.of': '{used} / {limit}',
  'owner.usage.ofUnlimited': '{used} · bez ograničenja',

  // ---- Company list -----------------------------------------------------
  // Two counted phrases handed to one frame, as on the dashboard.
  'owner.companies.summary': '{total} · {suspended}',
  'owner.companies.filter.aria': 'Status firme',
  'owner.companies.filter.all': 'Sve',
  'owner.companies.filter.active': 'Aktivne',
  'owner.companies.filter.suspended': 'Obustavljene',
  'owner.companies.col.company': 'Firma',
  'owner.companies.col.seats': 'Mjesta',
  'owner.companies.col.activity': 'Posljednja aktivnost',
  'owner.companies.create': 'Otvori firmu',

  // ---- One customer's workspace ----------------------------------------
  'owner.workspace.loading': 'Učitavanje firme…',
  'owner.workspace.back': 'Sve firme',
  'owner.workspace.notFound.title': 'Firma nije pronađena',
  'owner.workspace.notFound.description': 'Možda je link star ili je adresa pogrešno prepisana.',
  'owner.workspace.customerSince': 'klijent od {date}',
  'owner.workspace.impersonate': 'Prijavi se kao…',
  'owner.workspace.tab.overview': 'Pregled',
  'owner.workspace.tab.analytics': 'Analitika',
  'owner.workspace.tab.people': 'Korisnici',
  'owner.workspace.tab.access': 'Pristup',
  'owner.workspace.tab.sessions': 'Sesije',
  'owner.workspace.tab.activity': 'Dnevnik',
  'owner.workspace.tab.integration': 'Integracija',
  'owner.workspace.tab.settings': 'Postavke',

  /*
   * Suspending. The body spells out every way in that closes, because the
   * question the next phone call asks is "can they still use the card".
   * The reason is shown to the customer's people word for word, and the
   * hint under the field says so before anybody types a note to a colleague
   * into it.
   */
  'owner.suspend.action': 'Obustavi',
  'owner.suspend.title': 'Obustaviti pristup za {name}?',
  'owner.suspend.body':
    'Svi korisnici ove firme odmah se odjavljuju i ne mogu se ponovo prijaviti — ni lozinkom, ni karticom, ni aktivacionim kodom — dok firmu ponovo ne aktivirate. Podaci ostaju netaknuti.',
  'owner.suspend.reason': 'Razlog (nije obavezno)',
  'owner.suspend.reason.hint': 'Prikazuje se doslovno na ekranu za prijavu svakom korisniku firme.',
  'owner.suspend.reason.placeholder': 'npr. Na zahtjev uprave firme, do daljnjeg.',
  'owner.suspend.confirm': 'Obustavi pristup',
  'owner.suspend.sessionsEnded.one': 'Prekinuta je {count} sesija.',
  'owner.suspend.sessionsEnded.few': 'Prekinute su {count} sesije.',
  'owner.suspend.sessionsEnded.many': 'Prekinuto je {count} sesija.',
  'owner.reactivate.action': 'Ponovo aktiviraj',
  'owner.reactivate.title': 'Ponovo aktivirati {name}?',
  'owner.reactivate.body':
    'Korisnici se ponovo mogu prijaviti. Sesije prekinute pri obustavi se ne vraćaju — svako se prijavljuje iznova.',
  'owner.reactivate.confirm': 'Aktiviraj',
  // No full stop after the date: the Bosnian date already ends in one.
  'owner.suspension.title': 'Pristup je obustavljen',
  'owner.suspension.since': 'Pristup obustavljen od {date}',
  'owner.suspension.reason': 'Korisnici pri prijavi vide: „{reason}“',
  'owner.suspension.noReason': 'Razlog nije naveden — korisnici vide samo da je pristup obustavljen.',

  // ---- Workspace: overview ----------------------------------------------
  // Label-and-number pairs ("Administratora: 3") rather than counted nouns,
  // so a figure in a pill never needs three forms to read correctly.
  'owner.overview.loading': 'Učitavanje pregleda…',
  'owner.overview.seats': 'Mjesta',
  'owner.overview.seats.hint': 'Aktivni korisnici koji nisu vlasnici, prema ograničenju na kartici Pristup.',
  'owner.overview.storage': 'Prostor',
  'owner.overview.storage.hint': 'Zbir veličine svih priloga ove firme.',
  'owner.overview.online': 'Trenutno u aplikaciji',
  'owner.overview.online.hint': 'Koristili su je u posljednjih pet minuta.',
  'owner.overview.mes': 'KaproMES',
  'owner.overview.mes.notConfigured': 'Nije podešen',
  'owner.overview.mes.moduleOff': 'Podešen, modul isključen',
  'owner.overview.mes.failing.one': '{count} neuspjela isporuka',
  'owner.overview.mes.failing.few': '{count} neuspjele isporuke',
  'owner.overview.mes.failing.many': '{count} neuspjelih isporuka',
  'owner.overview.mes.ok': 'Radi',
  'owner.overview.mes.lastSync': 'Posljednja sinhronizacija {when}',
  'owner.overview.mes.neverSynced': 'Još nijednom sinhronizovan',
  'owner.overview.people.title': 'Ljudi',
  'owner.overview.people.description': 'Od izdatih naloga do onih koji ih zaista koriste.',
  'owner.overview.people.total': 'Naloga ukupno',
  'owner.overview.people.active': 'Aktivnih',
  'owner.overview.people.last30': 'Prijavljeno u 30 dana',
  'owner.overview.people.last7': 'Prijavljeno u 7 dana',
  'owner.overview.people.never': 'Nikad se nisu prijavili: {count}',
  'owner.overview.people.pendingPassword': 'Čekaju vlastitu lozinku: {count}',
  'owner.overview.people.admins': 'Administratora: {count}',
  'owner.overview.activity.title': 'Aktivnost',
  'owner.overview.activity.description': 'Kada je šta posljednji put urađeno.',
  'owner.overview.activity.lastSignIn': 'Posljednja prijava na sistem',
  'owner.overview.activity.lastSubmission': 'Posljednja prijava',
  'owner.overview.activity.lastEvaluation': 'Posljednja 5S evaluacija',
  'owner.overview.activity.moduleOff': 'modul isključen',
  'owner.overview.activity.submissions30': 'Prijava u 30 dana: {count} · otvorenih zadataka: {tasks}',
  'owner.overview.activity.evaluations30': 'Evaluacija u 30 dana: {count}',

  // ---- Workspace: people ------------------------------------------------
  'owner.people.description': 'Preuzimanje naloga i prekid sesija bilježe se u dnevniku firme.',
  'owner.people.search.aria': 'Pretraga korisnika',
  'owner.people.inactive': 'neaktivan',
  'owner.people.lastLogin': 'posljednji put {when}',
  'owner.people.neverLoggedIn': 'nikad prijavljen',
  'owner.people.endSessions.action': 'Odjavi',
  'owner.people.endSessions.aria': 'Odjavi korisnika {name} sa svih uređaja',
  'owner.people.endSessions.title': 'Odjaviti korisnika {name} sa svih uređaja?',
  // Says what this does *not* do, because the two get confused on the phone:
  // ending sessions is for a lost device, deactivating is for a person gone.
  'owner.people.endSessions.body':
    'Sve otvorene sesije ovog korisnika prekidaju se odmah, na svakom uređaju. Nalog ostaje aktivan i može se ponovo prijaviti — za izgubljen telefon to je dovoljno, a nalog osobe koja je otišla deaktivira administrator firme.',
  'owner.people.endSessions.confirm': 'Odjavi sa svih uređaja',

  // ---- Impersonation reason ---------------------------------------------
  'owner.impersonate.reason': 'Razlog',
  'owner.impersonate.reason.hint':
    '{count} / {max} znakova, najmanje {min}. Upisuje se u dnevnik firme i vidi ga njihov administrator.',
  'owner.impersonate.reason.placeholder': 'npr. Ne vidi svoj zadatak od jutros — razgovor s administratorom.',
  'owner.impersonate.confirm': 'Prijavi se kao {name}',

  // ---- Workspace: sessions ----------------------------------------------
  'owner.sessions.loading': 'Učitavanje sesija…',
  'owner.sessions.count.one': '{count} otvorena sesija',
  'owner.sessions.count.few': '{count} otvorene sesije',
  'owner.sessions.count.many': '{count} otvorenih sesija',
  'owner.sessions.summary': 'Sada u aplikaciji: {online} · različitih osoba: {people}',
  'owner.sessions.online': 'Sada u aplikaciji',
  'owner.sessions.offline': 'Nije korištena posljednjih pet minuta',
  'owner.sessions.col.person': 'Korisnik',
  'owner.sessions.col.device': 'Uređaj',
  'owner.sessions.col.device.explain':
    'Pročitano iz podataka pregledača, pa je okvirno. Tačan zapis vidite kad zadržite pokazivač; ispod je naziv koji je uređaj sam dao pri prijavi.',
  'owner.sessions.col.ip': 'IP adresa',
  'owner.sessions.col.lastUsed': 'Posljednja upotreba',
  'owner.sessions.col.actions': 'Radnje',
  'owner.sessions.device.unknown': 'Nepoznat uređaj',
  'owner.sessions.kind.phone': 'telefon',
  'owner.sessions.kind.tablet': 'tablet',
  'owner.sessions.kind.desktop': 'računar',
  'owner.sessions.kind.unknown': 'nepoznata vrsta uređaja',
  'owner.sessions.impersonation': 'Podrška',
  'owner.sessions.neverUsed': 'još nekorištena',
  'owner.sessions.opened': 'otvorena {when}',
  'owner.sessions.expires': 'Ističe {when}',
  'owner.sessions.empty.title': 'Niko nije prijavljen',
  'owner.sessions.empty.description': 'Ova firma trenutno nema nijednu otvorenu sesiju.',
  'owner.sessions.endOne.title': 'Prekinuti ovu sesiju?',
  'owner.sessions.endOne.body':
    '{name} se odjavljuje na uređaju „{device}“. Na ostalim uređajima ostaje prijavljen.',
  'owner.sessions.endOne.confirm': 'Prekini sesiju',
  'owner.sessions.endOne.aria': 'Prekini sesiju korisnika {name} ({device})',
  'owner.sessions.endUser.body.one':
    'Prekida se {count} sesija korisnika {name}. Nalog ostaje aktivan i može se ponovo prijaviti.',
  'owner.sessions.endUser.body.few':
    'Prekidaju se {count} sesije korisnika {name}. Nalog ostaje aktivan i može se ponovo prijaviti.',
  'owner.sessions.endUser.body.many':
    'Prekida se {count} sesija korisnika {name}. Nalog ostaje aktivan i može se ponovo prijaviti.',
  // The one reached for in a panic, so it names how many people it is about
  // to sign out before anybody confirms it.
  'owner.sessions.endAll.action': 'Odjavi sve',
  'owner.sessions.endAll.title': 'Odjaviti sve korisnike firme {name}?',
  'owner.sessions.endAll.body.one':
    'Sa svih uređaja odjavljuje se {count} osoba (otvorenih sesija: {sessions}). Moraće se ponovo prijaviti; podaci se ne gube.',
  'owner.sessions.endAll.body.few':
    'Sa svih uređaja odjavljuju se {count} osobe (otvorenih sesija: {sessions}). Moraće se ponovo prijaviti; podaci se ne gube.',
  'owner.sessions.endAll.body.many':
    'Sa svih uređaja odjavljuje se {count} osoba (otvorenih sesija: {sessions}). Moraće se ponovo prijaviti; podaci se ne gube.',
  'owner.sessions.endAll.confirm': 'Odjavi sve',

  // ---- Workspace: activity log ------------------------------------------
  'owner.activity.loading': 'Učitavanje dnevnika…',
  'owner.activity.title': 'Dnevnik firme',
  'owner.activity.description': 'Izmjene njihovih ljudi i sve što je odavde urađeno u vezi s njima.',
  'owner.activity.search.placeholder': 'Pretraži opis',
  'owner.activity.search.aria': 'Pretraga dnevnika',
  'owner.activity.from': 'Od datuma',
  'owner.activity.to': 'Do datuma',
  'owner.activity.ownerOnly': 'Samo radnje vlasnika',
  'owner.activity.ownerOnly.explain':
    'Obustave, izmjene podešavanja, preuzimanja naloga s razlogom, prekidi sesija i MES testovi — sve što je urađeno iz ovog panela.',
  'owner.activity.clear': 'Očisti filtere',
  'owner.activity.empty.title': 'Nema zapisa',
  'owner.activity.empty.description': 'U dnevniku ove firme još nema ničega.',
  'owner.activity.empty.filtered': 'Nijedan zapis ne odgovara filterima.',
  'owner.activity.ownerBadge': 'Vlasnik',
  // Labels for what an owner action recorded; see ActivityTab.
  'owner.activity.prop.reason': 'Razlog',
  'owner.activity.prop.settingsChanged': 'Promijenjena podešavanja',
  'owner.activity.prop.userName': 'Korisnik',
  'owner.activity.prop.device': 'Uređaj',
  'owner.activity.prop.ended': 'Prekinuto sesija',
  'owner.activity.causer': '{name} ({code})',
  'owner.activity.system': 'Sistem',
  'owner.activity.impersonatedBy': 'kao podrška: {name}',

  /*
   * Workspace: access.
   *
   * Every switch carries two sentences. The visible one says what *off* does
   * to the customer's people, because that is the direction with
   * consequences; the one behind the question mark is what somebody wants to
   * know before flipping it — usually that nothing is deleted.
   */
  'owner.access.modules.title': 'Moduli',
  'owner.access.modules.description':
    'Koji dijelovi aplikacije postoje za ovu firmu. Isključivanje ništa ne briše.',
  'owner.access.module.submissions.off':
    'Kad je isključen: radnici ne mogu prijaviti kvar ni prijedlog, a rukovodioci ne vide red prijava ni zadatke.',
  'owner.access.module.submissions.hint':
    'Nestaju iz menija, kao i dugme za novu prijavu. Ništa se ne briše — uključite ga ponovo i sve je na svom mjestu.',
  'owner.access.module.fiveS.off': 'Kad je isključen: nema 5S obilazaka, obrazaca ni 5S analitike.',
  'owner.access.module.fiveS.hint':
    'Ocjenjivači gube dugme za novi obilazak, a administrator karticu 5S obrasci. Postojeće ocjene ostaju sačuvane.',
  'owner.access.module.analytics.off': 'Kad je isključen: rukovodioci ne vide ekran Analitika svoje firme.',
  'owner.access.module.analytics.hint':
    'Ne utiče na prijave ni na 5S — skriva se samo njihov pregled i trendovi.',
  'owner.access.module.mes.off':
    'Kad je isključen: noćna sinhronizacija i slanje događaja u MES staju.',
  'owner.access.module.mes.hint':
    'Pristupni podaci ostaju sačuvani, a administrator firme ne vidi karticu Integracija.',

  'owner.access.security.title': 'Prijava i sigurnost',
  'owner.access.security.description':
    'Kako se ljudi ove firme prijavljuju i koliko dugo ostaju prijavljeni.',
  'owner.access.security.twoFactor': 'Dvofaktorska zaštita za administratore',
  'owner.access.security.twoFactor.description':
    'Administrator bez nje se i dalje prijavljuje, ali mu administracija ostaje zaključana dok je ne uključi na svom profilu.',
  'owner.access.security.twoFactor.hint':
    'Odnosi se samo na ulogu administratora — radnici i rukovodioci se prijavljuju kao i dosad.',
  'owner.access.security.password': 'Prijava lozinkom',
  'owner.access.security.password.description':
    'Kad je isključeno, radnici i rukovodioci se prijavljuju samo QR karticom. Administratori uvijek mogu lozinkom.',
  'owner.access.security.password.hint':
    'Prva prijava aktivacionim kodom radi uvijek, bez obzira na ovo — tako nalog nastaje.',
  'owner.access.security.badge': 'Prijava QR karticom',
  'owner.access.security.badge.description':
    'Kad je isključeno, štampane kartice prestaju raditi i svako se prijavljuje šifrom i lozinkom.',
  'owner.access.security.badge.hint':
    'Kartice ostaju izdate i ponovo rade čim ovo uključite — ništa ne treba ponovo štampati.',
  'owner.access.security.lastMethod': 'Barem jedan način prijave mora ostati uključen.',
  'owner.access.security.sessionDays': 'Najduža sesija (dana)',
  'owner.access.security.sessionDays.hint':
    'Koliko dugo „Zapamti me“ drži korisnika prijavljenim, od 1 do 90. Bez toga sesija traje 12 sati. Važi za nove prijave.',

  'owner.access.limits.title': 'Ograničenja',
  'owner.access.limits.description': 'Koliko ljudi i prostora ova firma može koristiti.',
  'owner.access.limits.seats': 'Broj mjesta',
  'owner.access.limits.seats.hint': 'Aktivni korisnici koji nisu vlasnici. Prazno znači bez ograničenja.',
  'owner.access.limits.seats.explain':
    'Kad su mjesta popunjena, administrator firme ne može dodati ni ponovo aktivirati korisnika. Noćna MES sinhronizacija se ne zaustavlja — samo prijavi prekoračenje.',
  'owner.access.limits.storage': 'Prostor za priloge (MB)',
  'owner.access.limits.storage.hint': 'Zbir svih priloga firme. Prazno znači bez ograničenja.',
  'owner.access.limits.storage.explain':
    'Kad se prostor popuni, slanje novih slika i videa se odbija. Postojeći prilozi ostaju.',
  'owner.access.limits.unlimited': 'Bez ograničenja',
  'owner.access.limits.inUse': 'Trenutno zauzeto',

  // What a save takes away, one line each, listed in the confirmation.
  'owner.access.loss.module': 'Modul „{module}“ nestaje iz njihove aplikacije.',
  'owner.access.loss.password': 'Radnici i rukovodioci se više ne mogu prijaviti lozinkom — samo karticom.',
  'owner.access.loss.badge': 'Štampane QR kartice prestaju raditi.',
  'owner.access.loss.twoFactor':
    'Administratori bez dvofaktorske zaštite ostaju bez administracije dok je ne uključe.',
  'owner.access.loss.seats':
    'Ograničenje ({limit}) je ispod broja aktivnih korisnika ({used}) — niko se ne deaktivira, ali novi se ne mogu dodati.',
  'owner.access.confirm.title': 'Sačuvati promjene za {name}?',
  'owner.access.confirm.body': 'Ovo se odmah odražava na ljude ove firme:',
  'owner.access.confirm.action': 'Sačuvaj promjene',
  'owner.access.dirty': 'Imate nesačuvane promjene.',
  'owner.access.clean': 'Sve je sačuvano.',
  'owner.access.reset': 'Poništi',
  'owner.access.saved': 'Pristup je sačuvan.',
  'owner.access.help':
    'Ovdje se ništa ne briše: isključen modul i ograničenje samo zatvaraju vrata, a podaci ostaju i vraćaju se čim se promjena poništi. Svaka izmjena se bilježi u dnevniku firme.',

  // ---- Workspace: integration and settings ------------------------------
  'owner.mes.panel.title': 'KaproMES integracija',
  'owner.mes.panel.description': 'Adresa, pristupni podaci i događaji koje šaljemo u njihov MES.',
  'owner.mes.moduleOff.title': 'Modul je isključen',
  'owner.mes.moduleOff.body':
    'Podešavanja su sačuvana, ali noćna sinhronizacija i slanje događaja preskaču ovu firmu dok se modul ne uključi na kartici Pristup.',
  'owner.mes.locked.edit':
    'Vaša uloga ({role}) može testirati vezu i pokrenuti sinhronizaciju, ali ne i mijenjati podatke. Mijenjati mogu: {editors}.',
  'owner.mes.locked.all':
    'Vaša uloga ({role}) ovdje samo gleda. Testirati i sinhronizovati mogu: {runners}; mijenjati: {editors}.',
  'owner.settings.title': 'Podaci firme',
  'owner.settings.description': 'Naziv, šifra, kontakti, logo i valuta.',

  // ---- Team -------------------------------------------------------------
  /*
   * Still blunt about what is being handed over: every role reads every
   * customer's data, and copy that read like an ordinary "add user" form is
   * what would lead to the account being treated like an ordinary one.
   */
  'owner.team.title': 'Tim',
  'owner.team.subtitle': 'Ljudi koji vode platformu i šta ko smije.',
  'owner.team.invite': 'Pozovi člana',
  'owner.team.invite.description': 'Nalog u vlasničkom panelu, s ulogom koju odaberete.',
  'owner.team.invite.warning':
    'Svaki član tima vidi podatke svih firmi. Šta smije mijenjati zavisi od uloge — dodijelite najmanju koja je dovoljna. Pozivnicu šaljemo na e-mail; lozinku postavlja sam i niko je ne vidi.',
  'owner.team.invite.employeeCode.hint': 'Time se prijavljuje.',
  'owner.team.invite.submit': 'Pošalji pozivnicu',
  'owner.team.role': 'Uloga',
  'owner.team.role.aria': 'Uloga — {name}',
  'owner.team.panel.description': 'Promjena uloge važi odmah, i u već otvorenim sesijama.',
  'owner.team.you': 'vi',
  'owner.team.status.active': 'aktivan',
  'owner.team.status.inactive': 'deaktiviran',
  'owner.team.status.invited': 'čeka pozivnicu',
  'owner.team.lock.self':
    'Vlastitu ulogu ne možete mijenjati niti sebe deaktivirati — to radi drugi član s punim pristupom.',
  'owner.team.lock.lastFull':
    'Ovo je jedini aktivni član s punim pristupom. Prvo nekom drugom dodijelite puni pristup, inače niko ne bi mogao vratiti promjenu.',
  'owner.team.deactivate.action': 'Deaktiviraj',
  'owner.team.deactivate.title': 'Deaktivirati {name}?',
  'owner.team.deactivate.body':
    'Odmah gubi pristup vlasničkom panelu. Nalog ostaje i možete ga ponovo aktivirati.',
  'owner.team.deactivate.confirm': 'Deaktiviraj',
  'owner.team.activate.action': 'Aktiviraj',

  // ---- Customer health ----------------------------------------------
  /*
   * The score on a customer's Pregled tab, the pill in the company list and
   * the portfolio. `owner.score` rather than `owner.health`, which already
   * names the platform's own Sistem screen.
   *
   * Levels describe the company, and *firma* and *tvrtka* are both feminine,
   * so the adjectives agree in all three local standards. *Tiha* for dormant
   * matches the alert raised for the same customers — "Klijent je utihnuo" —
   * so the pill and the inbox do not name one state twice.
   */
  'owner.score.title': 'Zdravlje klijenta',
  'owner.score.description': 'Koriste li ono što su kupili — sažeto u jednu ocjenu od 0 do 100.',
  'owner.score.explain':
    'Ponderisani prosjek mjerenja ispod. Mjerenje koje se ne može izračunati ne ulazi u ocjenu, a njegova težina se dijeli ostalima. Računa se najviše jednom u deset minuta.',
  'owner.score.loading': 'Računanje ocjene…',
  'owner.score.computedAt': 'izračunato {when}',
  'owner.score.lastActivity': 'Posljednja aktivnost {when}',
  'owner.score.noActivity': 'Još nikakve aktivnosti',
  'owner.score.ring.aria': 'Ocjena zdravlja {score} od 100 — {level}',
  'owner.score.components': 'Od čega se ocjena sastoji',
  'owner.score.weight': 'težina {weight}',
  'owner.score.dropped.moduleOff': 'modul isključen',
  'owner.score.dropped.noData': 'nema podataka',

  'owner.score.level.healthy': 'Zdrava',
  'owner.score.level.watch': 'Za praćenje',
  'owner.score.level.atRisk': 'U riziku',
  'owner.score.level.dormant': 'Tiha',
  'owner.score.level.new': 'Nova',
  'owner.score.level.suspended': 'Obustavljena',
  'owner.score.level.healthy.description':
    'Ocjena 70 ili više: koriste sistem, šire ga i odgovaraju na prijave.',
  'owner.score.level.watch.description': 'Ocjena od 45 do 69: koriste sistem, ali nešto popušta.',
  'owner.score.level.atRisk.description':
    'Ocjena ispod 45: korištenje je slabo ili pada — vrijedi ih nazvati.',
  'owner.score.level.dormant.description': 'Niko ništa nije uradio u posljednjih 30 dana.',
  'owner.score.level.new.description': 'Klijent kraće od 21 dan — uvođenje, bez uzbune.',
  'owner.score.level.suspended.description':
    'Pristup je obustavljen — tišina je naša odluka, ne njihova.',

  // Each hint says what is counted, over which window, and where the full
  // and the zero marks are — the three things anybody arguing with a
  // component asks first.
  'owner.score.component.recency': 'Posljednja aktivnost',
  'owner.score.component.reach': 'Doseg',
  'owner.score.component.momentum': 'Zamah',
  'owner.score.component.responsiveness': 'Odziv na prijave',
  'owner.score.component.fiveS': '5S pokrivenost',
  'owner.score.component.recency.hint':
    'Dani od posljednje aktivnosti bilo koje vrste — prijave, 5S obilaska, recenzije, završenog zadatka ili prijave na sistem. Puni bodovi do 3 dana, nula od 30 dana.',
  'owner.score.component.reach.hint':
    'Udio aktivnih korisnika koji su u posljednjih 30 dana bilo šta uradili. Puni bodovi od 60%.',
  'owner.score.component.momentum.hint':
    'Aktivnost u posljednjih 30 dana podijeljena s aktivnošću u prethodnih 30. Puni bodovi kad nije pala, nula kad je stala.',
  'owner.score.component.responsiveness.hint':
    'Udio prijava iz posljednjih 90 dana na koje je prvi odgovor stigao u roku od 72 sata — isto mjerenje kao u njihovoj Analitici. Puni bodovi od 90%.',
  'owner.score.component.fiveS.hint':
    'Udio aktivnih radnih centara s 5S obilaskom u posljednjih 30 dana. Puni bodovi od 80%.',
  'owner.score.component.dropped':
    'Ovo mjerenje se za ovog klijenta ne može izračunati, pa ne ulazi u ocjenu.',
  'owner.score.component.responsiveness.dropped.moduleOff':
    'Modul Prijave je za ovog klijenta isključen, pa odziv na prijave ne ulazi u ocjenu.',
  'owner.score.component.responsiveness.dropped.noData':
    'U posljednjih 90 dana nema prijava starijih od 72 sata po kojima bi se odziv mogao ocijeniti, pa ne ulazi u ocjenu.',
  'owner.score.component.fiveS.dropped.moduleOff':
    'Modul 5S je za ovog klijenta isključen, pa 5S pokrivenost ne ulazi u ocjenu.',
  'owner.score.component.fiveS.dropped.noData':
    'Firma nema nijedan aktivan radni centar, pa 5S pokrivenost ne ulazi u ocjenu.',
  // The raw measure beside each bar, in its own unit.
  'owner.score.value.today': 'danas',
  'owner.score.value.days.one': 'prije {count} dan',
  'owner.score.value.days.few': 'prije {count} dana',
  'owner.score.value.days.many': 'prije {count} dana',
  'owner.score.value.ratio': '{ratio}× prethodnih 30 dana',
  'owner.score.value.reach': '{percent} korisnika',
  'owner.score.value.responsiveness': '{percent} prijava',
  'owner.score.value.fiveS': '{percent} centara',

  'owner.score.warnings.title': 'Upozorenja',
  'owner.score.warnings.none': 'Nema upozorenja.',
  'owner.score.warnings.open': 'Otvori karticu {tab}',
  'owner.score.severity.critical': 'Hitno',
  'owner.score.severity.warn': 'Upozorenje',
  'owner.score.severity.info': 'Napomena',

  /*
   * One sentence per warning, from the raw numbers the server sends. Counted
   * nouns in three forms; percentages arrive already formatted ("24%").
   */
  'owner.score.warning.dormant.one': 'Bez ikakve aktivnosti već {count} dan.',
  'owner.score.warning.dormant.few': 'Bez ikakve aktivnosti već {count} dana.',
  'owner.score.warning.dormant.many': 'Bez ikakve aktivnosti već {count} dana.',
  'owner.score.warning.dormant.never': 'Niko još nikad ništa nije uradio.',
  'owner.score.warning.activityDrop':
    'Aktivnost je pala s {previous} na {current} — posljednjih 30 dana prema prethodnih 30.',
  'owner.score.warning.lowReach':
    'Samo {percent} aktivnih korisnika uradilo je bilo šta u posljednjih 30 dana.',
  'owner.score.warning.neverActivated.one': '{count} korisnik ({percent}) nikad se nije prijavio.',
  'owner.score.warning.neverActivated.few': '{count} korisnika ({percent}) nikad se nisu prijavila.',
  'owner.score.warning.neverActivated.many': '{count} korisnika ({percent}) nikad se nije prijavilo.',
  'owner.score.warning.backlogOverdue.one': '{count} otvorena prijava je prekoračila rok.',
  'owner.score.warning.backlogOverdue.few': '{count} otvorene prijave su prekoračile rok.',
  'owner.score.warning.backlogOverdue.many': '{count} otvorenih prijava je prekoračilo rok.',
  'owner.score.warning.fiveSCoverageLow':
    'Samo {percent} aktivnih radnih centara imalo je 5S obilazak u posljednjih 30 dana.',
  'owner.score.warning.seatsNearlyFull': 'Zauzeto je {used} od {limit} mjesta.',
  'owner.score.warning.storageNearlyFull': 'Prostor za priloge: {used} od {quota}.',
  'owner.score.warning.mesSyncStale': 'Posljednja MES sinhronizacija bila je prije {duration}.',
  'owner.score.warning.adminWithoutTwoFactor.one':
    '{count} administrator nema dvofaktorsku zaštitu, a obavezna je.',
  'owner.score.warning.adminWithoutTwoFactor.few':
    '{count} administratora nemaju dvofaktorsku zaštitu, a obavezna je.',
  'owner.score.warning.adminWithoutTwoFactor.many':
    '{count} administratora nema dvofaktorsku zaštitu, a obavezna je.',
  'owner.score.warning.unknown': 'Upozorenje sa servera: {key}',

  // ---- Company list: health and activity ----------------------------
  'owner.companies.col.health': 'Zdravlje',
  'owner.companies.sort.aria': 'Redoslijed',
  'owner.companies.sort.name': 'Po nazivu',
  'owner.companies.sort.health': 'Po zdravlju',
  'owner.companies.lastActivity': 'aktivnost {when}',
  'owner.companies.noActivity': 'još bez aktivnosti',
  'owner.companies.online': '{count} sada',
  'owner.companies.warnings.one': '{count} upozorenje',
  'owner.companies.warnings.few': '{count} upozorenja',
  'owner.companies.warnings.many': '{count} upozorenja',

  // ---- Workspace: analytics -----------------------------------------
  /*
   * Every section and every chart is captioned with the question it answers;
   * the `.hint` behind the question mark says what is counted and over which
   * window. A chart whose question could not be written did not get drawn.
   */
  // No full stop after the date: the local date already ends in one.
  'owner.insights.window': 'Cijeli mjeseci po lokalnom vremenu fabrike, od {since}',
  'owner.insights.moduleOff.title': 'Modul je isključen',
  'owner.insights.moduleOff.body':
    'Modul „{module}“ je isključen na kartici Pristup, pa ovdje nema šta crtati. Podaci iz vremena dok je bio uključen nisu obrisani.',

  'owner.insights.adoption.title': 'Usvajanje',
  'owner.insights.adoption.question': 'Koriste li ljudi naloge koje su dobili — i koliko njih svaki mjesec?',
  'owner.insights.funnel.title': 'Od naloga do korištenja',
  'owner.insights.funnel.question': 'Koliko se izdatih naloga zaista koristi?',
  'owner.insights.funnel.hint':
    'Aktivni nalozi koji nisu vlasnički. Aktivan znači da je osoba nešto uradila — prijavu, 5S obilazak, recenziju, završen zadatak ili prijavu na sistem.',
  'owner.insights.funnel.accounts': 'Aktivni nalozi',
  'owner.insights.funnel.activated': 'Ikad se prijavili',
  'owner.insights.funnel.active30': 'Aktivni u 30 dana',
  'owner.insights.funnel.active7': 'Aktivni u 7 dana',
  'owner.insights.funnel.empty': 'Firma nema nijedan aktivan nalog.',
  'owner.insights.contributors.title': 'Aktivni ljudi po mjesecu',
  'owner.insights.contributors.question': 'Širi li se korištenje ili ga nosi nekoliko ljudi?',
  'owner.insights.contributors.hint': 'Različite osobe s bilo kakvom aktivnošću u tom mjesecu.',
  'owner.insights.contributors.empty': 'Niko nije ništa uradio u ovom periodu.',
  'owner.insights.series.contributors': 'Aktivnih ljudi',

  'owner.insights.process.title': 'Proces',
  'owner.insights.process.question': 'Obrađuju li se prijave na vrijeme i postaju li uštede?',
  'owner.insights.stat.open': 'Otvorene prijave',
  'owner.insights.stat.overdue': 'Prekoračen rok',
  'owner.insights.stat.implemented': 'Provedena poboljšanja',
  'owner.insights.stat.implemented.detail': 'od {proposed} predloženih',
  'owner.insights.stat.savings': 'Ostvarene uštede',
  'owner.insights.stat.onTime': 'Zadaci na vrijeme',
  'owner.insights.submissions.title': 'Prijave po mjesecu',
  'owner.insights.submissions.question': 'Koliko se prijavljuje, i šta?',
  'owner.insights.submissions.hint': 'Prijave po mjesecu u kojem su podnesene, po vrsti.',
  'owner.insights.submissions.empty': 'Nijedna prijava u ovom periodu.',
  'owner.insights.response.title': 'Odziv i rješavanje',
  'owner.insights.response.question': 'Koliko brzo stiže prvi odgovor, a koliko traje rješavanje?',
  'owner.insights.response.hint':
    'Medijan po mjesecu prijave: od prijave do prvog odgovora i do rješenja — isto mjerenje kao u njihovoj Analitici.',
  'owner.insights.response.empty': 'Nijedna prijava iz ovog perioda nije dobila odgovor.',
  'owner.insights.series.firstResponse': 'Prvi odgovor',
  'owner.insights.series.resolution': 'Rješenje',
  'owner.insights.unit.hours': '{count} h',
  'owner.insights.unit.days': '{count} d',
  'owner.insights.backlog.title': 'Otvorene prijave po starosti',
  'owner.insights.backlog.question': 'Gomila li se zaostatak?',
  'owner.insights.backlog.hint': 'Stanje danas, bez obzira na odabrani period.',
  'owner.insights.backlog.summary': 'otvoreno {open} · rok prekoračen {overdue}',
  'owner.insights.backlog.empty': 'Nema otvorenih prijava.',
  'owner.insights.backlog.lt7': 'do 7 dana',
  'owner.insights.backlog.lt30': '7–30 dana',
  'owner.insights.backlog.lt90': '30–90 dana',
  'owner.insights.backlog.gte90': '90+ dana',
  'owner.insights.series.reports': 'Prijave',
  'owner.insights.improvements.title': 'Poboljšanja i uštede',
  'owner.insights.improvements.question': 'Postaju li prijedlozi stvarne uštede?',
  'owner.insights.improvements.hint':
    'Prijedlozi podneseni u odabranom periodu i uštede po datumu provedbe, u valuti klijenta.',
  'owner.insights.improvements.pipeline': 'Od prijedloga do provedbe',
  'owner.insights.improvements.proposed': 'predloženo',
  'owner.insights.improvements.approved': 'odobreno',
  'owner.insights.improvements.implemented': 'provedeno',
  'owner.insights.improvements.estimated': 'Procjena',
  'owner.insights.improvements.actual': 'Ostvareno',
  'owner.insights.improvements.savings': 'Uštede',
  'owner.insights.improvements.cost': 'Troškovi',
  'owner.insights.savings.empty': 'Nijedno poboljšanje nije provedeno u ovom periodu.',
  'owner.insights.series.savings': 'Uštede u mjesecu',
  'owner.insights.series.cumulative': 'Ukupno',
  'owner.insights.tasks.title': 'Zadaci',
  'owner.insights.tasks.question': 'Završavaju li se zadaci na vrijeme?',
  'owner.insights.tasks.hint':
    'Zadaci završeni u odabranom periodu, i koliko ih je završeno do roka. Prekoračeni otvoreni zadaci su stanje danas.',
  'owner.insights.tasks.empty': 'Nijedan zadatak u ovom periodu.',
  'owner.insights.tasks.completed': 'Završeno',
  'owner.insights.tasks.onTime': 'Od toga na vrijeme',
  'owner.insights.tasks.overdueOpen': 'Otvoreni, rok prošao',

  'owner.insights.fiveS.title': '5S',
  'owner.insights.fiveS.question': 'Je li red na radnim mjestima sve bolji, i obilaze li se svi centri?',
  'owner.insights.fiveS.centres': 'aktivnih radnih centara: {count}',
  'owner.insights.fiveS.empty': 'Nijedan 5S obilazak u ovom periodu.',
  'owner.insights.fiveSTrend.title': 'Prosjek i pokrivenost',
  'owner.insights.fiveSTrend.question': 'Raste li ocjena, i koliki dio centara se obilazi?',
  'owner.insights.fiveSTrend.hint':
    'Prosječna ocjena 5S obilazaka u mjesecu, i udio aktivnih radnih centara obiđenih taj mjesec.',
  'owner.insights.walks.title': 'Obilasci po mjesecu',
  'owner.insights.walks.question': 'Koliko se 5S obilazaka zaista obavi?',
  'owner.insights.walks.hint': 'Predani 5S obilasci po mjesecu obilaska.',
  'owner.insights.series.average': 'Prosječna ocjena',
  'owner.insights.series.coverage': 'Pokrivenost',
  'owner.insights.series.walks': 'Obilasci',

  'owner.insights.usage.title': 'Korištenje',
  'owner.insights.usage.question': 'Kada, gdje i na čemu rade u aplikaciji?',
  'owner.insights.heatmap.title': 'Kada rade',
  'owner.insights.heatmap.question': 'U koje dane i u koje sate ljudi rade u aplikaciji?',
  'owner.insights.heatmap.hint':
    'Prijave, 5S obilasci, recenzije i završeni zadaci u posljednjih 90 dana, po lokalnom vremenu fabrike. Prijave na sistem nisu uključene.',
  'owner.insights.heatmap.empty': 'Nikakve aktivnosti u posljednjih 90 dana.',
  'owner.insights.heatmap.peak': 'Najviše: {day}, {from}:00–{to}:00 ({count})',
  'owner.insights.heatmap.cell': '{day}, {from}:00–{to}:00: {count}',
  'owner.insights.heatmap.hours': 'Sat u danu, lokalno vrijeme fabrike',
  'owner.insights.heatmap.less': 'manje',
  'owner.insights.heatmap.more': 'više',
  'owner.insights.plants.title': 'Po pogonu',
  'owner.insights.plants.question': 'Koji pogon nosi korištenje, a koji zaostaje?',
  'owner.insights.plants.hint':
    'Posljednjih 90 dana. Aktivni ljudi su oni koji su u tom pogonu nešto uradili.',
  'owner.insights.plants.empty': 'Nikakve aktivnosti po pogonima u posljednjih 90 dana.',
  'owner.insights.plants.plant': 'Pogon',
  'owner.insights.plants.submissions': 'Prijave',
  'owner.insights.plants.evaluations': '5S',
  'owner.insights.plants.activeUsers': 'Ljudi',
  'owner.insights.plants.none': 'Bez pogona',
  'owner.insights.modules.title': 'Moduli',
  'owner.insights.modules.question': 'Koriste li ono što je uključeno?',
  'owner.insights.modules.hint':
    'Radnje u posljednjih 30 dana. Prijave: nove prijave i promjene statusa. 5S: obilasci. KaproMES: uspješne sinhronizacije i isporuke, gdje se bilježe. Analitika se ne mjeri — otvaranje ekrana se nigdje ne bilježi.',
  'owner.insights.modules.on': 'uključen',
  'owner.insights.modules.off': 'isključen',
  'owner.insights.modules.notTracked': 'ne mjeri se',
  'owner.insights.modules.actions.one': '{count} radnja',
  'owner.insights.modules.actions.few': '{count} radnje',
  'owner.insights.modules.actions.many': '{count} radnji',
  'owner.insights.devices.title': 'Uređaji',
  'owner.insights.devices.question': 'Rade li s telefona uz mašinu ili iz kancelarije?',
  'owner.insights.devices.hint':
    'Sesije otvorene u odabranom periodu, prema podacima pregledača. Uređaji se bilježe od {since}.',
  'owner.insights.devices.hint.never':
    'Sesije otvorene u odabranom periodu, prema podacima pregledača.',
  'owner.insights.devices.empty': 'Još nijedna sesija sa zapisanim uređajem.',
  'owner.insights.devices.unknown': 'nepoznato',
  'owner.insights.devices.total': 'ukupno sesija: {count}',
  'owner.insights.devices.sessions.one': '{count} sesija',
  'owner.insights.devices.sessions.few': '{count} sesije',
  'owner.insights.devices.sessions.many': '{count} sesija',

  /*
   * Weekdays for the heatmap. Client-owned because /api/meta has months but
   * not days, and not taken from `Intl` because Bosnian formats through
   * sr-Latn-RS, which would put the Serbian "sre" on a Bosnian screen.
   */
  'owner.weekday.short.1': 'Pon',
  'owner.weekday.short.2': 'Uto',
  'owner.weekday.short.3': 'Sri',
  'owner.weekday.short.4': 'Čet',
  'owner.weekday.short.5': 'Pet',
  'owner.weekday.short.6': 'Sub',
  'owner.weekday.short.7': 'Ned',
  'owner.weekday.1': 'ponedjeljak',
  'owner.weekday.2': 'utorak',
  'owner.weekday.3': 'srijeda',
  'owner.weekday.4': 'četvrtak',
  'owner.weekday.5': 'petak',
  'owner.weekday.6': 'subota',
  'owner.weekday.7': 'nedjelja',

  // ---- Portfolio ----------------------------------------------------
  'owner.portfolio.benchmarks.title': 'Medijani',
  'owner.portfolio.benchmarks.description':
    'Srednja vrijednost među aktivnim klijentima koji su prošli uvođenje — polovina je iznad, polovina ispod.',
  'owner.portfolio.title': 'Klijenti jedan pored drugog',
  'owner.portfolio.description':
    'Klik na red otvara klijenta. Klik na naslov kolone poreda po njoj — prvo oni kojima treba pažnja.',
  'owner.portfolio.empty': 'Još nema nijednog klijenta.',
  'owner.portfolio.sort.aria': 'Poredaj po koloni: {column}',
  'owner.portfolio.worse': 'lošije od medijana ({median})',
  'owner.portfolio.legend.worse': 'lošije od medijana svih klijenata',
  'owner.portfolio.legend.moduleOff': 'modul isključen — brojka je od ranije i ne poredi se',
  'owner.portfolio.moduleOff':
    'Modul je za ovog klijenta isključen. Brojka je iz vremena dok je bio uključen i ne poredi se s medijanom.',
  'owner.portfolio.benchmarks.customers.one': 'od {count} klijenta',
  'owner.portfolio.benchmarks.customers.few': 'od {count} klijenta',
  'owner.portfolio.benchmarks.customers.many': 'od {count} klijenata',
  'owner.portfolio.trend.up': 'više nego prethodnih 30 dana',
  'owner.portfolio.trend.down': 'manje nego prethodnih 30 dana',
  'owner.portfolio.trend.flat': 'isto kao prethodnih 30 dana',
  'owner.portfolio.trend.none': 'nema prethodnih 30 dana za poređenje',
  'owner.portfolio.metric.company': 'Firma',
  'owner.portfolio.metric.health': 'Zdravlje',
  'owner.portfolio.metric.health.hint':
    'Ocjena zdravlja od 0 do 100 — od čega je sastavljena piše na kartici Pregled svakog klijenta.',
  'owner.portfolio.metric.reach': 'Doseg',
  'owner.portfolio.metric.reach.hint':
    'Udio aktivnih korisnika s bilo kakvom aktivnošću u posljednjih 30 dana.',
  'owner.portfolio.metric.submissions': 'Prijave, 30 dana',
  'owner.portfolio.metric.submissions.hint':
    'Prijave podnesene u posljednjih 30 dana; strelica je promjena u odnosu na prethodnih 30.',
  'owner.portfolio.metric.response': 'Medijan odziva',
  'owner.portfolio.metric.response.hint':
    'Medijan vremena od prijave do prvog odgovora, za prijave iz posljednjih 90 dana.',
  'owner.portfolio.metric.overdue': 'Prekoračen rok',
  'owner.portfolio.metric.overdue.hint': 'Otvorene prijave kojima je rok prošao, stanje danas.',
  'owner.portfolio.metric.fiveSAverage': '5S prosjek',
  'owner.portfolio.metric.fiveSAverage.hint': 'Prosječna ocjena 5S obilazaka u posljednjih 30 dana.',
  'owner.portfolio.metric.fiveSCoverage': '5S pokrivenost',
  'owner.portfolio.metric.fiveSCoverage.hint':
    'Udio aktivnih radnih centara obiđenih u posljednjih 30 dana.',
  'owner.portfolio.metric.savings': 'Uštede ove godine',
  'owner.portfolio.metric.savings.hint':
    'Ostvarene uštede poboljšanja provedenih ove kalendarske godine, u valuti klijenta.',
  'owner.portfolio.metric.lastActivity': 'Posljednja aktivnost',
  'owner.portfolio.metric.lastActivity.hint':
    'Bilo šta: prijava, 5S obilazak, recenzija, završen zadatak ili prijava na sistem. Zelena tačka znači da je neko u aplikaciji upravo sada.',
  'owner.portfolio.platform.title': 'Cijela platforma',
  'owner.portfolio.platform.description': 'Sve firme zajedno, kroz odabrani period.',

  // ---- Phase 3: what is recorded, and since when ---------------------
  /*
   * Everything below reads history that only began to be kept with phase 3,
   * so most screens open by saying since when. Before the first row they
   * explain instead of printing zeros: "nobody failed" and "nobody was
   * counting" are the same number and opposite facts.
   */
  'owner.status.unknown': 'Nepoznato ({value})',
  'owner.status.unknown.hint': 'Ova vrijednost je novija od ove verzije aplikacije.',
  'owner.duration.ms': '{count} ms',
  'owner.duration.seconds': '{count} s',
  'owner.duration.minutes': '{minutes} min {seconds} s',
  'owner.duration.hours': '{hours} h {minutes} min',
  'owner.due.now': 'sada',
  'owner.due.minutes': 'za {count} min',
  'owner.due.hours': 'za {count} h',
  'owner.due.days.one': 'za {count} dan',
  'owner.due.days.few': 'za {count} dana',
  'owner.due.days.many': 'za {count} dana',

  // ---- Workspace: security -----------------------------------------
  /*
   * *Prijava* is both a report and a sign-in in this application. Here it is
   * always the sign-in, and the screen says "pokušaj prijave" wherever the
   * attempt rather than the success is meant, so the two never blur.
   */
  'owner.workspace.tab.security': 'Sigurnost',
  'owner.security.loading': 'Učitavanje prijava…',
  // No full stop after the date: the local date already ends in one.
  'owner.security.window': 'Dani po lokalnom vremenu fabrike, od {since}',
  'owner.security.recordingSince': 'Pokušaji prijave bilježe se od {date}',
  'owner.security.recordingSince.partial':
    'Pokušaji prijave bilježe se tek od {date} — dani prije toga nisu prazni, nego nezabilježeni.',
  'owner.security.period.aria': 'Period',
  'owner.security.period.one': 'Posljednji {count} dan',
  'owner.security.period.few': 'Posljednja {count} dana',
  'owner.security.period.many': 'Posljednjih {count} dana',
  'owner.security.notRecorded.title': 'Prijave se još ne bilježe',
  'owner.security.notRecorded.description':
    'Bilježenje pokušaja prijave počinje s ovom verzijom. Prvi pokušaj nakon toga — uspješan ili ne — pojavit će se ovdje. Do tada nema šta prikazati: nula bi značila „niko nije pogriješio“, a istina je „niko nije brojao“.',

  'owner.security.stat.succeeded': 'Uspješne prijave',
  'owner.security.stat.succeeded.hint':
    'Prijave koje su završile ulaskom u aplikaciju u odabranom periodu, lozinkom ili karticom. Ispod: koliko je različitih ljudi ušlo, od svih aktivnih naloga.',
  'owner.security.stat.succeeded.detail': '{signedIn} od {accounts} ljudi',
  'owner.security.stat.failed': 'Neuspjele',
  'owner.security.stat.failed.hint':
    'Pokušaji s pogrešnim podatkom: nepoznata šifra ili kartica, pogrešna lozinka ili kod, neaktivan nalog. Ispod: odbijeni (tačni podaci, zatvorena vrata) i blokirani (previše pokušaja).',
  'owner.security.stat.failed.detail': 'odbijeno {refused} · blokirano {throttled}',
  'owner.security.stat.rate': 'Stopa neuspjeha',
  'owner.security.stat.rate.hint':
    'Neuspjeli pokušaji od neuspjelih i uspješnih zajedno. Do 15% je uobičajeno jer ljudi griješe u kucanju; od 30% je ili pogrešno podijeljena lozinka ili neko ko nije njihov.',
  'owner.security.rate.normal': 'uobičajeno',
  'owner.security.rate.elevated': 'povišeno',
  'owner.security.rate.high': 'visoko',
  'owner.security.rate.none': 'nema pokušaja',
  'owner.security.stat.twoFactor': '2FA administratora',
  'owner.security.stat.twoFactor.value': '{covered} / {admins}',
  'owner.security.stat.twoFactor.hint':
    'Aktivni administratori s uključenom dvofaktorskom zaštitom. Kad je obavezna, administrator bez nje ne može u administraciju. Svi nalozi: {covered} od {accounts}.',
  'owner.security.twoFactor.required': 'obavezna',
  'owner.security.twoFactor.optional': 'nije obavezna',
  'owner.security.stat.badges': 'QR kartice',
  'owner.security.stat.badges.hint':
    'Aktivni nalozi kojima je izdata QR kartica za prijavu. Ispod: uspješne prijave lozinkom i karticom u odabranom periodu.',
  'owner.security.stat.badges.detail': 'lozinkom {password} · karticom {badge}',
  'owner.security.stat.lastSuccess': 'Posljednja uspješna',
  'owner.security.stat.lastSuccess.hint':
    'Posljednja uspješna prijava bilo koga iz ove firme, bez obzira na odabrani period.',

  'owner.security.chart.title': 'Prijave po danu',
  'owner.security.chart.question': 'Ulaze li ljudi bez problema, i kad je bilo neuspjeha?',
  'owner.security.chart.hint':
    'Svaki pokušaj prijave po lokalnom danu i ishodu. Zahtjev za drugi korak se ne crta: njegov ishod je sljedeći zapis. Dani prije početka bilježenja nisu nacrtani.',
  'owner.security.chart.empty': 'Nijedan pokušaj prijave u ovom periodu.',
  'owner.security.chart.aria':
    'Pokušaji prijave po danu, {days} dana: uspješnih {succeeded}, neuspjelih {failed}, odbijenih {refused}, blokiranih {throttled}.',
  'owner.security.series.succeeded': 'Uspješne',
  'owner.security.series.failed': 'Neuspjele',
  'owner.security.series.refused': 'Odbijene',
  'owner.security.series.throttled': 'Blokirane',

  'owner.security.failures.one': '{count} neuspio pokušaj',
  'owner.security.failures.few': '{count} neuspjela pokušaja',
  'owner.security.failures.many': '{count} neuspjelih pokušaja',
  'owner.security.showAttempts': 'Pokušaji',
  'owner.security.showAttempts.person': 'Prikaži pokušaje prijave korisnika {name}',
  'owner.security.showAttempts.ip': 'Prikaži pokušaje prijave s adrese {ip}',

  'owner.security.repeated.title': 'Ponovljeni neuspjesi',
  'owner.security.repeated.question': 'Ko ne može ući — ili ko pokušava tuđim nalogom?',
  'owner.security.repeated.hint':
    'Ljudi s tri ili više neuspjelih pokušaja u odabranom periodu, najviše deset, oni s najviše neuspjeha prvi. Ime otvara osobu na kartici Korisnici.',
  'owner.security.repeated.none': 'Niko nije imao tri ili više neuspjelih pokušaja.',
  'owner.security.repeated.throttled': 'blokirano: {count}',
  'owner.security.repeated.lastFailure': 'posljednji neuspjeh {when}',
  'owner.security.repeated.lastSuccess': 'posljednja uspješna prijava {when}',
  'owner.security.repeated.noSuccess': 'bez ijedne uspješne prijave',

  'owner.security.afterFailures.title': 'Uspjeh nakon niza neuspjeha',
  'owner.security.afterFailures.question': 'Je li neko pogodio lozinku?',
  'owner.security.afterFailures.hint':
    'Uspješna prijava kojoj je u satu prije nje prethodilo pet ili više neuspjelih pokušaja na isti nalog. Može biti i sama osoba koja se sjetila iz šestog puta — zato je treba pitati.',
  'owner.security.afterFailures.alert':
    'Ovako izgleda pogođena lozinka. Provjerite s tom osobom da li je to bila ona; ako nije, prekinite joj sesije i neka promijeni lozinku.',
  'owner.security.afterFailures.none': 'Nijedna uspješna prijava nije uslijedila nakon niza neuspjelih.',
  'owner.security.afterFailures.count': 'Neuspjelih pokušaja u satu prije: {count}',
  'owner.security.afterFailures.at': 'uspješna prijava {when}',

  'owner.security.ips.title': 'Adrese s najviše neuspjeha',
  'owner.security.ips.question': 'Odakle dolaze pogrešni pokušaji?',
  'owner.security.ips.hint':
    'IP adrese s tri ili više neuspjelih pokušaja na naloge ove firme u odabranom periodu, najviše deset. Mnogo naloga s jedne adrese znači da neko ide redom po spisku.',
  'owner.security.ips.none': 'Nijedna adresa nije imala tri ili više neuspjelih pokušaja.',
  'owner.security.ips.accounts': 'naloga: {count}',
  'owner.security.ips.lastAt': 'posljednji {when}',

  'owner.security.log.title': 'Dnevnik prijava',
  'owner.security.log.hint':
    'Svaki pokušaj prijave, najnoviji prvi. Ono što je upisano — lozinka, kod, kartica — nigdje se ne čuva, pa ga nema ni ovdje.',
  'owner.security.log.description':
    'Suzite po ishodu, osobi ili adresi. Pokušaji na nepoznatu šifru ili karticu nemaju osobu.',
  'owner.security.log.panel': 'Pokušaji prijave',
  'owner.security.log.loading': 'Učitavanje dnevnika prijava…',
  'owner.security.log.empty.title': 'Nema pokušaja prijave',
  'owner.security.log.empty.description': 'Za ovu firmu još nije zabilježen nijedan pokušaj prijave.',
  'owner.security.log.empty.filtered': 'Nijedan pokušaj ne odgovara filterima.',
  'owner.security.log.unknownAccount': 'nalog nije pronađen',
  'owner.security.log.unknownBadge': 'kartica nije prepoznata',
  'owner.security.col.when': 'Vrijeme',
  'owner.security.col.person': 'Korisnik',
  'owner.security.col.method': 'Način',
  'owner.security.col.outcome': 'Ishod',
  'owner.security.filter.outcome': 'Ishod',
  'owner.security.filter.allOutcomes': 'Svi ishodi',
  'owner.security.filter.reason': 'Razlog',
  'owner.security.filter.allReasons': 'Svi razlozi',
  'owner.security.filter.person': 'Korisnik',
  'owner.security.filter.everybody': 'Svi korisnici',
  'owner.security.filter.ip': 'IP adresa',
  'owner.security.filter.ip.placeholder': 'IP adresa',
  'owner.security.legend.title': 'Šta znače ishodi i razlozi',

  // One line per outcome and per reason, each with what it means for the
  // person on the other end of the phone.
  'owner.security.outcome.succeeded': 'Uspješna',
  'owner.security.outcome.succeeded.hint':
    'Osoba je ušla: podaci su bili tačni, a gdje se traži drugi korak, prošla je i njega.',
  'owner.security.outcome.challenged': 'Traži drugi korak',
  'owner.security.outcome.challenged.hint':
    'Lozinka ili kartica je tačna i zatražen je kod dvofaktorske zaštite. Ishod drugog koraka bilježi se kao poseban zapis.',
  'owner.security.outcome.failed': 'Neuspjela',
  'owner.security.outcome.failed.hint':
    'Pogrešan podatak — nepoznata šifra ili kartica, pogrešna lozinka ili kod, neaktivan nalog. Razlog stoji uz zapis.',
  'owner.security.outcome.refused': 'Odbijena',
  'owner.security.outcome.refused.hint':
    'Podaci su bili tačni, ali ulaz nije dozvoljen: firma je obustavljena ili je taj način prijave isključen.',
  'owner.security.outcome.throttled': 'Blokirana',
  'owner.security.outcome.throttled.hint':
    'Previše pokušaja u kratkom vremenu, pa server odbija bez provjere podataka. Zaštita od pogađanja lozinke; prestaje sama nakon kratkog vremena.',
  'owner.security.outcome.unknown': 'Nepoznat ishod ({value})',
  'owner.security.outcome.unknown.hint': 'Ovaj ishod je noviji od ove verzije aplikacije.',
  'owner.security.reason.unknownAccount': 'Nepoznata šifra',
  'owner.security.reason.unknownAccount.hint':
    'Nijedan nalog nema tu šifru zaposlenog. Ponovljeni pokušaji na istu nepoznatu šifru grupišu se bez čuvanja onoga što je upisano, jer ljudi ponekad upišu lozinku u polje za šifru.',
  'owner.security.reason.wrongPassword': 'Pogrešna lozinka',
  'owner.security.reason.wrongPassword.hint': 'Nalog postoji, lozinka nije tačna.',
  'owner.security.reason.inactiveAccount': 'Neaktivan nalog',
  'owner.security.reason.inactiveAccount.hint':
    'Lozinka je tačna, ali je nalog deaktiviran. Ponovo ga aktivira administrator firme.',
  'owner.security.reason.unknownBadge': 'Nepoznata kartica',
  'owner.security.reason.unknownBadge.hint':
    'Skenirana kartica ne pripada nijednom nalogu — poništena je, zamijenjena novom ili je tuđa.',
  'owner.security.reason.wrongCode': 'Pogrešan kod',
  'owner.security.reason.wrongCode.hint':
    'Šestocifreni kod iz aplikacije za dvofaktorsku zaštitu nije tačan ili je istekao.',
  'owner.security.reason.wrongRecoveryCode': 'Pogrešan rezervni kod',
  'owner.security.reason.wrongRecoveryCode.hint': 'Rezervni kod nije tačan ili je već iskorišten.',
  'owner.security.reason.challengeExpired': 'Drugi korak istekao',
  'owner.security.reason.challengeExpired.hint':
    'Drugi korak nije završen na vrijeme, ili se nalog u međuvremenu promijenio — deaktiviran je ili mu je isključena dvofaktorska zaštita.',
  'owner.security.reason.companySuspended': 'Firma obustavljena',
  'owner.security.reason.companySuspended.hint':
    'Podaci su tačni, ali je pristup firmi obustavljen. Razlog obustave korisnik vidi na ekranu za prijavu.',
  'owner.security.reason.methodDisabled': 'Način prijave isključen',
  'owner.security.reason.methodDisabled.hint':
    'Podaci su tačni, ali je taj način prijave — lozinka ili kartica — za ovu firmu isključen na kartici Pristup.',
  'owner.security.reason.unknown': 'Razlog: {value}',
  'owner.security.reason.unknown.hint': 'Ovaj razlog je noviji od ove verzije aplikacije.',
  'owner.security.method.password': 'Lozinka',
  'owner.security.method.badge': 'QR kartica',
  'owner.security.method.unknown': 'Nepoznato ({value})',
  'owner.security.secondFactor.totp': 'kod iz aplikacije',
  'owner.security.secondFactor.recoveryCode': 'rezervni kod',

  // ---- MES sync history (owner and admin Integracija) -------------------
  // Under `owner.mes` like the form beside it, though the customer's own
  // administrator reads them too — see components/MesIntegrationForm.tsx.
  'owner.mes.runs.title': 'Sinhronizacije',
  'owner.mes.runs.description':
    'Svako pokretanje sinhronizacije — noćno, iz vlasničkog panela ili od administratora firme.',
  'owner.mes.runs.hint':
    'Bilježi se od ove verzije. Aktivacioni kodovi koje je sinhronizacija izdala ovdje se samo broje — nikad se ne čuvaju.',
  'owner.mes.runs.none':
    'Još nijedna zabilježena sinhronizacija. Bilježe se od ove verzije, pa i integracija koja radi mjesecima ovdje počinje praznom; prvo noćno ili ručno pokretanje pojavit će se ovdje.',
  'owner.mes.runs.lastSuccess': 'Posljednja uspješna',
  'owner.mes.runs.lastFailure': 'Posljednja neuspjela',
  'owner.mes.runs.noFailure': 'nijedna',
  'owner.mes.runs.consecutive': 'Neuspjeha zaredom',
  'owner.mes.runs.consecutive.hint':
    'Neuspjela ili prekinuta pokretanja od posljednjeg uspješnog. Jedno je vrijedno pogleda; tri znače da se ljudi i mašine ne ažuriraju danima.',
  'owner.mes.runs.successRate': 'Uspješnost, 30 dana',
  'owner.mes.runs.successRate.hint': 'Udio završenih pokretanja u posljednjih 30 dana koja su uspjela.',
  'owner.mes.runs.severity.ok': 'u redu',
  'owner.mes.runs.severity.warn': 'pažnja',
  'owner.mes.runs.severity.critical': 'hitno',
  'owner.mes.runs.failingInARow.one': '{count} pokretanje zaredom nije uspjelo.',
  'owner.mes.runs.failingInARow.few': '{count} pokretanja zaredom nisu uspjela.',
  'owner.mes.runs.failingInARow.many': '{count} pokretanja zaredom nije uspjelo.',
  'owner.mes.runs.recent': 'Posljednjih pet',
  'owner.mes.runs.activations': 'izdato aktivacionih kodova: {count}',
  'owner.mes.runs.problems.one': '{count} problem',
  'owner.mes.runs.problems.few': '{count} problema',
  'owner.mes.runs.problems.many': '{count} problema',
  'owner.mes.runs.problemsMore': 'I još {count} — sačuvano je prvih 50.',
  'owner.mes.runs.showAll': 'Prikaži sve',
  'owner.mes.runs.hideAll': 'Sakrij',
  'owner.mes.runs.status.running': 'U toku',
  'owner.mes.runs.status.running.hint': 'Sinhronizacija je počela i još traje.',
  'owner.mes.runs.status.succeeded': 'Uspjela',
  'owner.mes.runs.status.succeeded.hint':
    'Završena bez greške. Problemi koje je našla — npr. mašine bez 5S obrasca — navedeni su ispod.',
  'owner.mes.runs.status.failed': 'Neuspjela',
  'owner.mes.runs.status.failed.hint':
    'Prekinuta greškom, najčešće jer MES nije odgovorio ili je odbio token. Poruka greške je ispod.',
  'owner.mes.runs.status.interrupted': 'Prekinuta',
  'owner.mes.runs.status.interrupted.hint':
    'Pokrenuta prije više od sat vremena i nikad završena — proces je ugašen usred rada, npr. restartom servera.',
  'owner.mes.runs.trigger.command': 'Noćna',
  'owner.mes.runs.trigger.command.hint': 'Zakazana sinhronizacija koju server pokreće svake noći.',
  'owner.mes.runs.trigger.owner': 'Vlasnički panel',
  'owner.mes.runs.trigger.owner.hint': 'Pokrenuta dugmetom „Sinhronizuj sada“ iz vlasničkog panela.',
  'owner.mes.runs.trigger.admin': 'Administrator firme',
  'owner.mes.runs.trigger.admin.hint': 'Pokrenuta dugmetom „Sinhronizuj sada“ na ekranu Integracija firme.',

  // ---- Workspace: storage and seats over time --------------------------
  // The tilde is the point: a straight line through thirty days is a
  // forecast, and the number must not read as a date somebody promised.
  'owner.usage.projection.storage.one': 'Kvota će biti puna za ~{count} dan.',
  'owner.usage.projection.storage.few': 'Kvota će biti puna za ~{count} dana.',
  'owner.usage.projection.storage.many': 'Kvota će biti puna za ~{count} dana.',
  'owner.usage.projection.storage.full': 'Kvota je već puna — nove slike i video se odbijaju.',
  'owner.usage.projection.seats.one': 'Mjesta će biti popunjena za ~{count} dan.',
  'owner.usage.projection.seats.few': 'Mjesta će biti popunjena za ~{count} dana.',
  'owner.usage.projection.seats.many': 'Mjesta će biti popunjena za ~{count} dana.',
  'owner.usage.projection.seats.full': 'Sva mjesta su popunjena — novi korisnik se ne može dodati.',
  'owner.usage.projection.short.one': '~{count} dan',
  'owner.usage.projection.short.few': '~{count} dana',
  'owner.usage.projection.short.many': '~{count} dana',
  'owner.usage.projection.short.full': 'već nema mjesta',
  'owner.usage.projection.storage.distant': 'Pri ovom rastu kvota neće biti puna ni za deset godina.',
  'owner.usage.projection.seats.distant': 'Pri ovom rastu mjesta neće biti popunjena ni za deset godina.',
  'owner.usage.projection.short.distant': 'više od 10 godina',
  'owner.usage.growth.storage': '{amount} dnevno',
  'owner.usage.growth.seats': '{amount} u 30 dana',
  'owner.usage.growth.flat': 'bez rasta',
  'owner.usage.history.notYet': 'Dnevni zapis počinje noćas — linija se pojavljuje od sutra.',
  'owner.usage.history.since': 'Bilježi se od {date} — za liniju trebaju bar dva dana.',
  'owner.usage.history.unavailable': 'Istorija trenutno nije dostupna.',
  'owner.usage.spark.storage': 'Zauzeti prostor kroz {days} dana: od {from} do {to}.',
  'owner.usage.spark.seats': 'Zauzeta mjesta kroz {days} dana: od {from} do {to}.',

  // ---- Sistem: recorded history ------------------------------------------
  'owner.system.loading': 'Učitavanje istorije…',
  'owner.system.recordingSince': 'Bilježi se od {date}',

  'owner.system.tasks.title': 'Zakazani zadaci',
  'owner.system.tasks.question': 'Radi li raspored — i je li svaki zadatak došao na red kad je trebao?',
  'owner.system.tasks.hint':
    'Svaki zadatak iz rasporeda aplikacije, redom kojim je zakazan: posljednje pokretanje, koliko je trajalo i kada je sljedeće.',
  'owner.system.tasks.count.one': '{count} zakazani zadatak',
  'owner.system.tasks.count.few': '{count} zakazana zadatka',
  'owner.system.tasks.count.many': '{count} zakazanih zadataka',
  'owner.system.tasks.notRecorded':
    'Pokretanja se još ne bilježe — prvo pokretanje rasporeda nakon ove verzije pojavit će se ovdje.',
  'owner.system.tasks.empty': 'Raspored aplikacije je prazan.',
  'owner.system.tasks.summary.late': 'kasni: {count}',
  'owner.system.tasks.summary.failing': 'neuspjelo: {count}',
  'owner.system.tasks.summary.ok': 'sve na vrijeme',
  'owner.system.tasks.col.task': 'Zadatak',
  'owner.system.task.remindOverdue': 'Jutarnji podsjetnik na kašnjenja',
  'owner.system.task.remindEvaluations': 'Podsjetnik na mjesečne 5S ocjene',
  'owner.system.task.syncMes': 'KaproMES sinhronizacija',
  'owner.system.task.sendMesReports': 'Ocjene i 5S pregled u KaproMES',
  'owner.system.task.snapshotUsage': 'Dnevni zapis prostora i mjesta',
  'owner.system.task.evaluateAlerts': 'Provjera upozorenja',
  'owner.system.task.alertDigest': 'Jutarnji pregled upozorenja e-poštom',
  'owner.system.task.pruneSignIns': 'Brisanje starih prijava',
  'owner.system.task.pruneHistory': 'Brisanje stare historije',
  'owner.system.tasks.col.status': 'Stanje',
  'owner.system.tasks.col.status.hint':
    'Uspio, Neuspio, Radi, Preskočen (prethodno pokretanje još traje ili ga je preuzeo drugi server) ili Prekinut (ugašen usred rada). „Kasni“ ima prednost: zadatak koji se prestao pokretati i dalje pokazuje zeleni ishod posljednjeg pokretanja.',
  'owner.system.tasks.col.lastRun': 'Posljednje pokretanje',
  'owner.system.tasks.col.duration': 'Trajanje',
  'owner.system.tasks.col.next': 'Sljedeće',
  'owner.system.tasks.col.next.hint':
    'Prema izrazu rasporeda, u njegovoj vremenskoj zoni. Tačno vrijeme je ispod pokazivača.',
  'owner.system.tasks.col.failures': 'Neuspjeha, 7 dana',
  'owner.system.tasks.col.failures.hint':
    'Neuspjela pokretanja u posljednjih sedam dana, od ukupnog broja pokretanja.',
  'owner.system.tasks.late': 'Kasni',
  'owner.system.tasks.late.hint':
    'Trebao je krenuti prije više od 15 minuta, a nijedno pokretanje nije zabilježeno. Najčešće znači da schedule:work ne radi.',
  'owner.system.tasks.neverRun': 'Još nije pokrenut',
  'owner.system.tasks.neverRun.hint': 'Od početka bilježenja ovaj zadatak još nijednom nije bio na redu.',
  'owner.system.tasks.exitCode': 'izlazni kod {code}',
  'owner.system.tasks.status.running': 'Radi',
  'owner.system.tasks.status.running.hint': 'Pokrenut i još traje. Zadatak u pozadini zatvara se kad završi.',
  'owner.system.tasks.status.succeeded': 'Uspio',
  'owner.system.tasks.status.succeeded.hint': 'Završen s izlaznim kodom 0.',
  'owner.system.tasks.status.failed': 'Neuspio',
  'owner.system.tasks.status.failed.hint':
    'Završen greškom ili izlaznim kodom različitim od 0. Poruka je ispod, ako je ima.',
  'owner.system.tasks.status.skipped': 'Preskočen',
  'owner.system.tasks.status.skipped.hint':
    'Raspored ga je preskočio jer prethodno pokretanje još traje ili ga je preuzeo drugi server — tako je i zamišljeno.',
  'owner.system.tasks.status.interrupted': 'Prekinut',
  'owner.system.tasks.status.interrupted.hint':
    'Pokrenut prije više od sat vremena i nikad završen — proces je ugašen usred rada.',

  'owner.system.mes.title': 'KaproMES sinhronizacije',
  'owner.system.mes.question': 'Stiže li noćna sinhronizacija do svakog klijenta koji je ima?',
  'owner.system.mes.panel': 'Klijenti s podešenim MES-om',
  'owner.system.mes.hint':
    'Svi klijenti s podešenom adresom MES-a, po nazivu. Naziv otvara njihovu karticu Integracija s cijelom istorijom.',
  'owner.system.mes.empty': 'Nijedan klijent nema podešen KaproMES.',
  'owner.system.mes.noRun': 'još bez zabilježenog pokretanja',
  'owner.system.mes.col.lastRun': 'Posljednje pokretanje',
  'owner.system.mes.col.deliveries': 'Isporuke',
  'owner.system.mes.col.deliveries.hint': 'Događaji poslani u njihov MES: neuspjeli i oni koji još čekaju.',
  'owner.system.mes.failed': 'neuspjelo {count}',
  'owner.system.mes.pending': 'čeka {count}',

  'owner.system.storage.title': 'Rast prostora',
  'owner.system.storage.question': 'Koliko brzo se disk puni, i ko ga puni?',
  'owner.system.storage.total': 'Prilozi ukupno',
  'owner.system.storage.total.hint': 'Zbir svih priloga svih klijenata, sada.',
  'owner.system.storage.growth': 'Rast',
  'owner.system.storage.growth.hint':
    'Zbir dnevnog rasta svih klijenata — prava linija kroz njihovih posljednjih 30 dana. Potrebno je najmanje sedam dnevnih zapisa.',
  'owner.system.storage.growth.none': 'još premalo dnevnih zapisa',
  'owner.system.storage.diskFull': 'Disk pun za',
  'owner.system.storage.diskFull.hint':
    'Slobodan prostor podijeljen ukupnim dnevnim rastom. Okvirno: prava linija ne zna za novog klijenta.',
  'owner.system.storage.diskFull.none': 'bez rasta ili disk nije mjerljiv',
  'owner.system.storage.byCompany': 'Po klijentu',
  'owner.system.storage.byCompany.hint':
    'Svaki klijent, najveći prvi: zauzeto sada, kvota, dnevni rast i kada će kvota biti puna.',
  'owner.system.storage.col.used': 'Zauzeto',
  'owner.system.storage.col.quota': 'Kvota',
  'owner.system.storage.col.full': 'Kvota puna za',

  'owner.system.signIns.title': 'Prijave na platformu',
  'owner.system.signIns.question': 'Ko pokušava ući, u koju firmu — i pokušava li neko ući u naš tim?',
  'owner.system.signIns.notRecorded':
    'Bilježenje pokušaja prijave počinje s ovom verzijom. Prvi pokušaj nakon toga, bilo kog klijenta ili vlasnika, pojavit će se ovdje.',
  'owner.system.signIns.challenged': 'traži drugi korak: {count}',
  'owner.system.signIns.chart.hint':
    'Svaki pokušaj na platformi po lokalnom danu: klijenti, naš tim i pokušaji koji ne pripadaju nikome.',
  'owner.system.signIns.owners.title': 'Prijave vlasnika',
  'owner.system.signIns.owners.hint':
    'Nalozi našeg tima otvaraju svakog klijenta, pa je i jedan neuspjeh vrijedan pogleda, a pet ili blokada je hitno.',
  'owner.system.signIns.owners.none': 'Nijedan neuspjeli pokušaj na naloge vlasnika u odabranom periodu.',
  'owner.system.signIns.owners.failed':
    'Neuspjelih pokušaja na naloge vlasnika: {failed} · blokirano: {throttled}',
  'owner.system.signIns.owners.lastFailure': 'Posljednji neuspjeh',
  'owner.system.signIns.owners.lastSuccess': 'Posljednja uspješna',
  'owner.system.signIns.unknown.title': 'Nepoznate šifre i kartice',
  'owner.system.signIns.unknown.hint':
    'Pokušaji koji nisu odgovarali nijednom nalogu, pa ne pripadaju nijednoj firmi. Ovdje se prvo vidi neko ko pogađa.',
  'owner.system.signIns.unknown.ips.hint': 'Adrese s najviše pokušaja na nepoznate šifre i kartice, najviše deset.',
  'owner.system.signIns.codes.title': 'Ponavljane nepoznate šifre',
  'owner.system.signIns.codes.hint':
    'Ista nepoznata šifra upisana tri ili više puta. Ključ je početak šifrovanog otiska onoga što je upisano — dovoljan da se vidi da je ista šifra pokušana četrdeset puta, a ne otkriva koja je.',
  'owner.system.signIns.codes.key': 'Ključ pokušaja — ne otkriva šta je upisano',
  'owner.system.signIns.codes.none': 'Nijedna nepoznata šifra nije ponovljena tri puta.',
  'owner.system.signIns.codes.attempts': 'pokušaja: {count}',
  'owner.system.signIns.byCompany': 'Po klijentu',
  'owner.system.signIns.byCompany.hint':
    'Svaki klijent po nazivu, za odabrani period. Naziv otvara njihovu karticu Sigurnost.',
  'owner.system.signIns.col.repeated': 'Ponovljeni neuspjesi',
  'owner.system.signIns.repeated': 'ljudi: {count}',

  // ---- Phase 4: the overview, rebuilt around alerts ------------------
  'owner.dashboard.system.aria': 'Stanje sistema',
  'owner.dashboard.system.open': '{label}: {value}. Otvori stranicu Sistem.',
  'owner.dashboard.system.unknown': 'još bez zapisa',
  'owner.dashboard.system.queue.ok': 'radi',
  'owner.dashboard.system.queue.backlog': 'posao čeka {minutes} min',
  'owner.dashboard.system.queue.failing': 'neuspjelih u 24 h: {count}',
  'owner.dashboard.system.mes.ok': 'radi kod svih ({count})',
  'owner.dashboard.system.mes.failing': 'ne uspijeva: {failing} od {configured}',
  'owner.dashboard.system.mes.none': 'nijedan klijent',
  'owner.dashboard.system.disk': 'Disk',
  'owner.dashboard.system.disk.free': 'slobodno {free}',
  'owner.dashboard.system.disk.full': 'pun',
  'owner.dashboard.system.disk.days.one': 'pun za ~{count} dan',
  'owner.dashboard.system.disk.days.few': 'pun za ~{count} dana',
  'owner.dashboard.system.disk.days.many': 'pun za ~{count} dana',

  // The strip under the alerts: one tile per customer, worst first.
  'owner.dashboard.customers.title': 'Klijenti',
  'owner.dashboard.customers.description': 'Prvi su oni kojima treba pažnja. Pločica otvara klijenta.',
  'owner.dashboard.customers.empty': 'Još nema nijednog klijenta.',
  'owner.dashboard.customers.alerts':
    'Otvorena upozorenja — hitnih: {critical}, upozorenja: {warn}, napomena: {info}',
  'owner.dashboard.customers.noAlerts': 'bez otvorenih upozorenja',

  /*
   * The pulse compares this month with the *same days* of the last one, and
   * the hint says so: on the 15th, half a month against a whole one would
   * read as a collapse every single month.
   */
  'owner.dashboard.pulse.title': 'Puls platforme',
  'owner.dashboard.pulse.description': 'Danas i ovaj mjesec, prema istim danima prošlog mjeseca. Samo klijenti.',
  'owner.dashboard.pulse.hint':
    '„Isti dani prošlog mjeseca“ znači od prvog do današnjeg dana u mjesecu, da se pola mjeseca ne poredi s cijelim. Linija je posljednjih 30 dana, po lokalnom vremenu.',
  'owner.dashboard.pulse.submissions': 'Prijave',
  'owner.dashboard.pulse.submissions.hint': 'Podnesene prijave svih klijenata.',
  'owner.dashboard.pulse.evaluations': '5S evaluacije',
  'owner.dashboard.pulse.evaluations.hint': 'Predani 5S obilasci svih klijenata.',
  'owner.dashboard.pulse.signIns': 'Prijave na sistem',
  'owner.dashboard.pulse.signIns.hint': 'Uspješne prijave u aplikaciju, lozinkom ili karticom.',
  'owner.dashboard.pulse.activePeople': 'Aktivni ljudi',
  'owner.dashboard.pulse.activePeople.hint':
    'Različiti ljudi klijenata koji su u periodu nešto uradili ili se uspješno prijavili.',
  'owner.dashboard.pulse.newUsers': 'Novi nalozi',
  'owner.dashboard.pulse.newUsers.hint': 'Nalozi klijenata otvoreni u periodu.',
  'owner.dashboard.pulse.today': 'danas',
  'owner.dashboard.pulse.thisMonth': 'ovaj mjesec',
  'owner.dashboard.pulse.month': 'ovaj mjesec: {count}',
  'owner.dashboard.pulse.change.up': '▲ {percent} više nego istih dana prošlog mjeseca ({previous})',
  'owner.dashboard.pulse.change.down': '▼ {percent} manje nego istih dana prošlog mjeseca ({previous})',
  'owner.dashboard.pulse.change.same': 'isto kao istih dana prošlog mjeseca ({previous})',
  'owner.dashboard.pulse.change.fromZero': 'istih dana prošlog mjeseca: 0',
  'owner.dashboard.pulse.spark': '{label} po danu, posljednjih {days} dana: ukupno {total}, najviše {peak} u jednom danu.',

  'owner.dashboard.activity.title': 'Šta je tim radio',
  'owner.dashboard.activity.description': 'Posljednjih 15 radnji vlasnika, kod svih klijenata.',
  'owner.dashboard.activity.empty': 'Još nijedne zabilježene radnje vlasnika.',
  'owner.dashboard.activity.impersonating': 'u sesiji podrške',
  'owner.dashboard.activity.impersonating.hint':
    'Urađeno dok je vlasnik bio prijavljen kao neko od ljudi klijenta.',
  'owner.dashboard.activity.open': 'Otvori dnevnik firme {name}',

  // ---- Alerts ---------------------------------------------------------
  /*
   * The words the owner team agreed on with the user: *preuzimam* says who
   * has it, *odgodi* puts it off to a date, *utišaj* stops one type for one
   * customer or everywhere. Alerts close themselves when the cause goes away;
   * nobody "closes" one by hand, which is why there is no such button.
   */
  'owner.alerts.title': 'Treba pažnju',
  'owner.alerts.description': 'Sve što provjere nađu, na jednom mjestu — kod klijenata i na platformi.',
  'owner.alerts.hint':
    'Provjere se pokreću svakih deset minuta. Upozorenje se samo zatvara kad uzrok nestane; kartica Riješeno pamti šta je bilo otvoreno i ko se time bavio.',
  'owner.alerts.loading': 'Učitavanje upozorenja…',
  'owner.alerts.tabs.aria': 'Stanje upozorenja',
  'owner.alerts.evaluatedAt': 'Provjereno {when}',
  'owner.alerts.notEvaluated': 'Provjere se još nisu pokrenule',
  'owner.alerts.evaluate': 'Provjeri sada',
  'owner.alerts.evaluated': 'Provjera je završena.',

  'owner.alerts.state.attention': 'Treba pažnju',
  'owner.alerts.state.attention.hint': 'Otvoreno, a niko ga još nije preuzeo ni odgodio.',
  'owner.alerts.state.acknowledged': 'Preuzeto',
  'owner.alerts.state.acknowledged.hint':
    'Neko iz tima se time bavi. Ako se stanje pogorša, vraća se u „Treba pažnju“.',
  'owner.alerts.state.snoozed': 'Odgođeno',
  'owner.alerts.state.snoozed.hint': 'Odgođeno do datuma, a ranije se vraća ako se stanje pogorša.',
  'owner.alerts.state.resolved': 'Riješeno',
  'owner.alerts.state.resolved.hint': 'Uzrok je nestao i provjera je upozorenje sama zatvorila.',

  'owner.alerts.source.health': 'Zdravlje klijenta',
  'owner.alerts.source.security': 'Sigurnost',
  'owner.alerts.source.mes': 'KaproMES',
  'owner.alerts.source.limits': 'Ograničenja',
  'owner.alerts.source.scheduler': 'Raspored',
  'owner.alerts.source.queue': 'Red čekanja',
  'owner.alerts.source.disk': 'Disk',

  'owner.alerts.filter.company': 'Klijent',
  'owner.alerts.filter.allCompanies': 'Svi klijenti',
  'owner.alerts.filter.severity': 'Ozbiljnost',
  'owner.alerts.filter.allSeverities': 'Sve ozbiljnosti',
  'owner.alerts.filter.source': 'Izvor',
  'owner.alerts.filter.allSources': 'Svi izvori',

  'owner.alerts.platform': 'Platforma',
  'owner.alerts.openedAt': 'otvoreno {when}',
  'owner.alerts.lastSeen': 'Posljednja provjera koja ga je našla: {when}',
  'owner.alerts.acknowledgedBy': 'Preuzeto: {name}, {when}',
  'owner.alerts.acknowledgedAnon': 'Preuzeto {when}',
  'owner.alerts.snoozedBy': 'Odgođeno do {until} — {name}',
  'owner.alerts.snoozedAnon': 'Odgođeno do {until}',
  'owner.alerts.resolvedAt': 'riješeno {when} · trajalo {duration}',
  'owner.alerts.resolvedAtShort': 'riješeno {when}',
  'owner.alerts.open.generic': 'Otvori',
  'owner.alerts.open.aria': '{target} — {title}',
  'owner.alerts.actions.aria': 'Radnje za upozorenje „{title}“',

  'owner.alerts.action.acknowledge': 'Preuzimam',
  'owner.alerts.action.unacknowledge': 'Poništi preuzimanje',
  'owner.alerts.action.snooze': 'Odgodi',
  'owner.alerts.action.snooze.days.one': '{count} dan',
  'owner.alerts.action.snooze.days.few': '{count} dana',
  'owner.alerts.action.snooze.days.many': '{count} dana',
  'owner.alerts.action.snooze.date': 'Do datuma…',
  'owner.alerts.action.unsnooze': 'Prekini odgodu',
  'owner.alerts.action.mute': 'Utišaj',

  'owner.alerts.done.acknowledged': 'Preuzeto. Tim vidi da se vi bavite ovim.',
  'owner.alerts.done.unacknowledged': 'Preuzimanje je poništeno.',
  'owner.alerts.done.snoozed': 'Odgođeno do {until}.',
  'owner.alerts.done.unsnoozed': 'Odgoda je prekinuta.',
  // "At the latest": whether the server closes it at once or on the next
  // run, this sentence is true.
  'owner.alerts.done.muted': 'Utišano. Upozorenje se zatvara najkasnije pri sljedećoj provjeri.',
  'owner.alerts.done.unmuted':
    'Utišavanje je ukinuto. Ako uzrok i dalje postoji, upozorenje se vraća pri sljedećoj provjeri.',

  'owner.alerts.snooze.title': 'Odgoditi do datuma',
  'owner.alerts.snooze.description':
    'Upozorenje se vraća u „Treba pažnju“ početkom tog dana — ili ranije, ako se stanje pogorša.',
  'owner.alerts.snooze.date': 'Datum',
  'owner.alerts.snooze.date.hint': 'Od sutra do {max}, najviše 30 dana.',
  'owner.alerts.snooze.confirm': 'Odgodi',

  /*
   * Muting says what it does *not* do as plainly as what it does: the check
   * keeps running, it simply stops telling anybody — here or by e-mail. That
   * is the half people misjudge when they reach for it to quieten a list.
   */
  'owner.alerts.mute.title': 'Utišati „{title}“?',
  'owner.alerts.mute.body':
    'Utišano upozorenje se zatvara i ne otvara ponovo dok se utišavanje ne ukine. Provjera i dalje radi — samo se o njoj više ne javlja, ni ovdje ni e-mailom.',
  'owner.alerts.mute.scope': 'Za koga',
  'owner.alerts.mute.scope.company': 'Samo za {company}',
  'owner.alerts.mute.scope.everywhere': 'Za sve klijente',
  'owner.alerts.mute.scope.platform': 'Ovo upozorenje se odnosi na cijelu platformu, pa se utišava svuda.',
  'owner.alerts.mute.reason': 'Razlog (nije obavezno)',
  'owner.alerts.mute.reason.hint': 'Vidi ga ostatak tima na spisku utišanih.',
  'owner.alerts.mute.reason.placeholder': 'npr. Klijent namjerno ne koristi 5S do proljeća.',
  'owner.alerts.mute.confirm': 'Utišaj',

  'owner.alerts.mutes.button': 'Utišano ({count})',
  'owner.alerts.mutes.title': 'Utišana upozorenja',
  'owner.alerts.mutes.description':
    'Vrste upozorenja koje se ne prikazuju, za jednog klijenta ili za sve. Kad ukinete utišavanje, upozorenje se vraća pri sljedećoj provjeri ako uzrok i dalje postoji.',
  'owner.alerts.mutes.loading': 'Učitavanje utišanih…',
  'owner.alerts.mutes.empty': 'Ništa nije utišano — prikazuje se sve što provjere nađu.',
  'owner.alerts.mutes.everywhere': 'Svi klijenti',
  'owner.alerts.mutes.created': 'utišano {when}',
  'owner.alerts.mutes.createdBy': 'utišano {when} — {name}',
  'owner.alerts.mutes.reason': '„{reason}“',
  'owner.alerts.mutes.unmute': 'Ukini',
  'owner.alerts.mutes.unmute.aria': 'Ukini utišavanje: {type} — {scope}',

  'owner.alerts.empty.attention.title': 'Ništa ne čeka',
  'owner.alerts.empty.attention': 'Posljednja provjera, {when}, nije našla ništa što čeka da ga neko preuzme.',
  'owner.alerts.empty.never':
    'Provjere se još nisu pokrenule. Pokreću se svakih deset minuta — ako ovo stoji duže, pogledajte raspored na stranici Sistem.',
  'owner.alerts.empty.acknowledged': 'Niko trenutno ništa nije preuzeo.',
  'owner.alerts.empty.snoozed': 'Ništa nije odgođeno.',
  'owner.alerts.empty.resolved': 'Još nijedno upozorenje nije riješeno.',
  'owner.alerts.empty.filtered': 'Nijedno upozorenje ne odgovara filterima.',
  'owner.alerts.pager.empty': 'Nema upozorenja',

  'owner.alerts.company.title': 'Upozorenja',
  'owner.alerts.company.description': 'Otvorena upozorenja ovog klijenta, iz svih provjera — ne samo iz ocjene zdravlja.',
  'owner.alerts.company.empty': 'Ovaj klijent nema otvorenih upozorenja.',

  // The nav badge, for a screen reader: the visible number alone says nothing.
  'owner.alerts.badge.one': '{count} upozorenje treba pažnju',
  'owner.alerts.badge.few': '{count} upozorenja trebaju pažnju',
  'owner.alerts.badge.many': '{count} upozorenja treba pažnju',
  'owner.alerts.badge.critical': '{attention}, od toga hitnih: {critical}',

  /*
   * One title per type in the catalogue. A title never depends on the
   * numbers, so the list of mutes — which has a type and no alert — names
   * what was silenced in the same words the inbox used.
   */
  'owner.alerts.type.health.dormant': 'Klijent je utihnuo',
  'owner.alerts.type.health.activityDrop': 'Aktivnost pada',
  'owner.alerts.type.health.lowReach': 'Malo ljudi koristi aplikaciju',
  'owner.alerts.type.health.neverActivated': 'Nalozi koji nikad nisu korišteni',
  'owner.alerts.type.health.backlogOverdue': 'Prijave preko roka',
  'owner.alerts.type.health.fiveSCoverageLow': 'Slaba 5S pokrivenost',
  'owner.alerts.type.health.seatsNearlyFull': 'Mjesta skoro popunjena',
  'owner.alerts.type.health.storageNearlyFull': 'Prostor skoro pun',
  'owner.alerts.type.health.mesSyncStale': 'MES sinhronizacija stoji',
  'owner.alerts.type.health.adminWithoutTwoFactor': 'Administrator bez dvofaktorske zaštite',
  'owner.alerts.type.health.unknown': 'Zdravlje klijenta: {key}',
  'owner.alerts.type.security.signInAfterFailures': 'Moguće pogođena lozinka',
  'owner.alerts.type.security.repeatedFailures': 'Ponovljene neuspjele prijave',
  'owner.alerts.type.security.ownerSignInFailures': 'Neuspjele prijave na naloge vlasnika',
  'owner.alerts.type.security.scanningAddress': 'Jedna adresa pokušava više naloga',
  'owner.alerts.type.security.unknownCodeAttempts': 'Pokušaji s nepoznatim šiframa',
  'owner.alerts.type.mes.syncFailing': 'MES sinhronizacija ne uspijeva',
  'owner.alerts.type.mes.deliveriesFailing': 'Isporuke prema MES-u ne prolaze',
  'owner.alerts.type.limits.storageFillsSoon': 'Kvota prostora se puni',
  'owner.alerts.type.limits.seatsFillSoon': 'Mjesta se popunjavaju',
  'owner.alerts.type.scheduler.late': 'Zakazani zadatak kasni',
  'owner.alerts.type.scheduler.failing': 'Zakazani zadatak ne uspijeva',
  'owner.alerts.type.queue.failedJobs': 'Neuspjeli poslovi u redu čekanja',
  'owner.alerts.type.disk.fillsSoon': 'Disk se puni',
  'owner.alerts.type.unknown': 'Upozorenje: {type}',

  /*
   * One line of facts per type, from the raw numbers. Counts that decline a
   * noun arrive already declined ("6 neuspjelih pokušaja"); counts that would
   * have to decline in an oblique case are written as "label: number", which
   * reads correctly at every count in every language.
   */
  'owner.alerts.fact.signInAfterFailures': 'Nalog {code}: {failures}, pa uspješna prijava {when}, s adrese {ip}.',
  'owner.alerts.fact.signInAfterFailures.noIp': 'Nalog {code}: {failures}, pa uspješna prijava {when}.',
  'owner.alerts.fact.repeatedFailures': 'Nalog {code}: {failures} u posljednja 24 sata, posljednji {when}.',
  'owner.alerts.fact.ownerSignInFailures':
    'Nalozi vlasnika u posljednja 24 sata — neuspjelih pokušaja: {failed}, blokiranih: {throttled}. Posljednji {when}.',
  'owner.alerts.fact.scanningAddress':
    'Adresa {ip} u posljednja 24 sata — neuspjelih pokušaja: {failures}, različitih naloga: {accounts}. Posljednji {when}.',
  'owner.alerts.fact.unknownCodeAttempts':
    'Neuspjelih pokušaja s nepoznatom šifrom ili karticom u posljednja 24 sata: {failed}. Posljednji {when}.',
  'owner.alerts.fact.mesSyncFailing': '{runs} Posljednja uspješna {when}.',
  'owner.alerts.fact.mesSyncFailing.never': '{runs} Nijedna još nije uspjela.',
  'owner.alerts.fact.mesDeliveriesFailing': 'Neuspjelih isporuka u posljednja 24 sata: {failed}. Posljednja {when}.',
  'owner.alerts.fact.storageUsed': 'Zauzeto {used} od {quota}.',
  'owner.alerts.fact.seatsUsed': 'Zauzeto {used} od {limit} mjesta.',
  'owner.alerts.fact.schedulerLate': 'Zadatak „{task}“ trebao je krenuti {when}, a nije pokrenut.',
  'owner.alerts.fact.schedulerFailing': 'Zadatak „{task}“ nije uspio {when}.',
  'owner.alerts.fact.queueFailedJobs': 'Neuspjelih poslova u posljednja 24 sata: {count}. Posljednji {when}.',
  'owner.alerts.fact.diskFree': 'Slobodno {free} od {total}.',
  'owner.alerts.fact.diskDays': 'Pri ovom rastu pun je za {days}.',
  'owner.alerts.fact.diskFull': 'Disk je pun.',
  'owner.alerts.fact.unknown': 'Ova vrsta upozorenja je novija od ove verzije aplikacije.',

  // ---- Team: my own alert e-mails ---------------------------------------
  'owner.alertPrefs.title': 'Moja upozorenja e-poštom',
  'owner.alertPrefs.description': 'Samo za vas — svaki član tima ovo bira za sebe.',
  'owner.alertPrefs.loading': 'Učitavanje podešavanja…',
  'owner.alertPrefs.immediate': 'Hitna upozorenja odmah',
  'owner.alertPrefs.immediate.description':
    'E-mail čim se otvori hitno upozorenje koje niko nije preuzeo ni odgodio — jedan po provjeri, koliko god ih bilo.',
  'owner.alertPrefs.digest': 'Jutarnji pregled',
  'owner.alertPrefs.digest.description':
    'Svako jutro u 7:30: šta je otvoreno, šta je preuzeto ili odgođeno, i šta se otvorilo i zatvorilo za 24 sata. Ne stiže kad nema šta reći.',
  'owner.alertPrefs.email': 'Šaljemo na {email}.',
  'owner.alertPrefs.noEmail':
    'Na vašem nalogu nema e-mail adrese, pa ova upozorenja ne mogu stići. Dodajte je na stranici Moj nalog.',
  'owner.alertPrefs.saved': 'Sačuvano.',

  // ---- The customer's glavni administrator --------------------------
  // Chosen here, by us. Until then every administrator of theirs keeps full
  // rights, which is why the warning names who would see nothing once that
  // ends: an administrator with no plant of their own.
  'owner.superAdmin.title': 'Glavni administrator',
  'owner.superAdmin.description':
    'Jedan po firmi: mijenja postavke, integraciju, pogone i administratore i vidi sve pogone. Dok nije izabran, svaki administrator firme ima puna prava.',
  'owner.superAdmin.current': 'Glavni administrator je {name}.',
  'owner.superAdmin.compat': 'Nije izabran — svi administratori imaju puna prava i vide sve pogone.',
  'owner.superAdmin.badge': 'Glavni administrator',
  'owner.superAdmin.choose': 'Postavi',
  'owner.superAdmin.choose.aria': 'Postavi korisnika {name} za glavnog administratora',
  'owner.superAdmin.access.all': 'Svi pogoni',
  'owner.superAdmin.access.none': 'Nijedan pogon',
  'owner.superAdmin.access.none.explain':
    'Kad firma dobije glavnog administratora, ovaj administrator ne vidi ništa dok mu se ne dodijeli pogon.',
  'owner.superAdmin.warning.one':
    '{count} administrator nema nijedan pogon i neće vidjeti ništa kad izaberete glavnog administratora: {names}.',
  'owner.superAdmin.warning.few':
    '{count} administratora nemaju nijedan pogon i neće vidjeti ništa kad izaberete glavnog administratora: {names}.',
  'owner.superAdmin.warning.many':
    '{count} administratora nema nijedan pogon i neće vidjeti ništa kad izaberete glavnog administratora: {names}.',
  'owner.superAdmin.empty': 'Firma nema nijednog aktivnog administratora.',
  'owner.superAdmin.confirm': 'Postaviti korisnika „{name}" za glavnog administratora?',
  'owner.superAdmin.confirm.body':
    'Od tada samo {name} mijenja postavke firme {company}, integraciju s KaproMES-om, pogone i administratore. Ostali administratori vide samo svoje pogone.',
  'owner.superAdmin.confirm.action': 'Postavi',
  'owner.superAdmin.saved': 'Glavni administrator je postavljen.',
  'owner.superAdmin.reset': 'Vrati na sve administratore',
  'owner.superAdmin.reset.confirm': 'Vratiti puna prava svim administratorima?',
  'owner.superAdmin.reset.body':
    '{name} više nije glavni administrator, a svaki administrator firme {company} ponovo ima puna prava i vidi sve pogone.',
  'owner.superAdmin.reset.done': 'Svi administratori ponovo imaju puna prava.',
} as const

export type OwnerSlice = Record<keyof typeof owner, string>
